import { html } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { MorningstarMedalistRating } from '../enums/morningstar-enum';

interface IconOptions {
  slot?: string;
  ariaLabel?: string;
  customClass?: string;
  analytics?: IconAnalyticsOptions;
}

interface IconAnalyticsOptions {
  dataMetricsClickable?: string;
  dataMetricsType?: string;
  dataMetricsLinkText?: string;
  dataMetricsLinkUrl?: string;
}

export class IconSVG {
  /**
   * Returns a lit `TemplateResult` for a download SVG icon.
   * The 'slot' attribute is added to the SVG element if the 'slot' parameter is provided.
   *
   * @param options - An object containing optional properties for the SVG element.
   * @returns A lit `TemplateResult` for the download SVG icon.
   */
  static download(options?: IconOptions) {
    return html`
      <svg
        slot=${ifDefined(options?.slot)}
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label=${ifDefined(options?.ariaLabel)}
        aria-hidden=${ifDefined(options?.ariaLabel ? undefined : 'true')}
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M13.4399 9.33158C13.7754 9.08864 13.8504 8.61973 13.6075 8.28424C13.3645 7.94875 12.8956 7.87372 12.5601 8.11666L9.75 10.1516V1C9.75 0.585786
           9.41421 0.25 9 0.25C8.58578 0.25 8.25 0.585786 8.25 1V10.1516L5.43988 8.11666C5.10439 7.87372 4.63548 7.94875 4.39254 8.28424C4.1496 8.61973
            4.22463 9.08863 4.56012 9.33157L8.56012 12.2281C8.82258 12.4182 9.17742 12.4182 9.43988 12.2281L13.4399 9.33158ZM17.75 12.5859C17.75 12.1717
             17.4142 11.8359 17 11.8359C16.5858 11.8359 16.25 12.1717 16.25 12.5859V16.2497H1.75V12.5859C1.75 12.1717 1.41421 11.8359 1 11.8359C0.585786
              11.8359 0.25 12.1717 0.25 12.5859V16.9997C0.25 17.4139 0.585787 17.7497 1 17.7497H17C17.4142 17.7497 17.75 17.4139 17.75 16.9997V12.5859Z"
          fill="black"
        />
      </svg>
    `;
  }

  /**
   * Returns a lit `TemplateResult` for an information (i) SVG icon.
   * The 'slot' attribute is added to the SVG element if the 'slot' parameter is provided.
   *
   * @param options - An object containing optional properties for the SVG element.
   * @returns A lit `TemplateResult` for the information SVG icon.
   */
  static info(options?: IconOptions) {
    return html`
      <svg
        slot=${ifDefined(options?.slot)}
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-label=${ifDefined(options?.ariaLabel)}
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M8 5.25C8.41421 5.25 8.75 4.91421 8.75 4.5C8.75 4.08579 8.41421 3.75 8
            3.75C7.58579 3.75 7.25 4.08579 7.25 4.5C7.25 4.91421 7.58579 5.25 8 5.25ZM8 6.25C7.72386 6.25 7.5 6.47386 7.5 6.75V11.75C7.5
            12.0261 7.72386 12.25 8 12.25C8.27614 12.25 8.5 12.0261 8.5 11.75V6.75C8.5 6.47386 8.27614 6.25 8 6.25Z"
          fill="black"
        ></path>
      </svg>
    `;
  }

  /**
   * Returns a lit `TemplateResult` for a caret facing down SVG icon.
   * The 'slot' attribute is added to the SVG element if the 'slot' parameter is provided.
   *
   * @param options - An object containing optional properties for the SVG element.
   * @returns A lit `TemplateResult` for the caret facing down SVG icon.
   */
  static caretDown(options?: IconOptions) {
    return html`
      <svg
        slot=${ifDefined(options?.slot)}
        width="20"
        height="20"
        viewBox="4 3 11 11"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 6L8 10L4 6"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
        ></path>
      </svg>
    `;
  }

  /**
   * Returns a lit `TemplateResult` for the magnifying glass SVG icon.
   * The 'slot' attribute is added to the SVG element if the 'slot' parameter is provided.
   *
   * @param options - An object containing optional properties for the SVG element.
   * @returns A lit `TemplateResult` for the magnifying glass SVG icon.
   */
  static magnify(options?: IconOptions) {
    return html`<svg
      slot="${ifDefined(options?.slot)}"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M1 7C1 3.96243 3.46243 1.5 6.5 1.5C9.53757 1.5 12 3.96243 12 7C12 10.0376 9.53757 12.5 6.5 12.5C3.46243 12.5 1 10.0376 1
        7ZM6.5 0.5C2.91015 0.5 0 3.41015 0 7C0 10.5899 2.91015 13.5 6.5 13.5C8.29352 13.5 9.91738 12.7736 11.0934 11.599C11.1122
        11.6922 11.1577 11.7812 11.23 11.8536L14.9801 15.6035C15.1753 15.7988 15.4919 15.7988 15.6872 15.6035C15.8824 15.4083 15.8824
        15.0917 15.6872 14.8964L11.9371 11.1464C11.8495 11.0588 11.7373 11.0104 11.6227 11.0015C12.4856 9.89831 13 8.50922 13 7C13
        3.41015 10.0899 0.5 6.5 0.5Z"
        fill="#0063AD"
      />
    </svg>`;
  }

  /**
   * Returns a lit `TemplateResult` for the filter button SVG icon.
   * The 'slot' attribute is added to the SVG element if the 'slot' parameter is provided.
   *
   * @param options - An object containing optional properties for the SVG element.
   * @returns A lit `TemplateResult` for the filter button SVG icon.
   */
  static filterBtn(options?: IconOptions) {
    return html`<svg
      slot="${ifDefined(options?.slot)}"
      width="17"
      height="18"
      viewBox="0 0 17 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M15.0332 2.40016C15.1847 2.19815 15.2091 1.92788 15.0962 1.70202C14.9833 1.47616 14.7524 1.3335 14.4999 1.3335H2.48723C2.23471
        1.3335 2.00387 1.47616 1.89094 1.70202C1.77801 1.92788 1.80238 2.19815 1.95389 2.40016L7.15923 9.39416V16.0002C7.15923 16.2527 7.3019
        16.4835 7.52775 16.5964C7.75361 16.7094 8.02388 16.685 8.22589 16.5335L9.55923 15.5335C9.7271 15.4076 9.82589 15.21 9.82589
        15.0002V9.3935L15.0332 2.40016Z"
        fill="white"
        stroke="white"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>`;
  }

  /**
   * Returns a lit `TemplateResult` for the linebreak SVG icon.
   * The 'slot' attribute is added to the SVG element if the 'slot' parameter is provided.
   *
   * @param options - An object containing optional properties for the SVG element.
   * @returns A lit `TemplateResult` for the linebreak SVG icon.
   */
  static lineBreak(options?: IconOptions) {
    return html`<svg
      slot="${ifDefined(options?.slot)}"
      width="1"
      height="30"
      viewBox="0 0 1 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <line x1="0.5" y1="30" x2="0.5" stroke="#7C8790" />
    </svg>`;
  }

  /**
   * Returns a lit `TemplateResult` for a popoverArrow SVG icon.
   * The 'slot' attribute is added to the SVG element if the 'slot' parameter is provided.
   *
   * @param options - An object containing optional properties for the SVG element.
   * @returns A lit `TemplateResult` for the popoverArrow SVG icon.
   */
  static popoverArrow(options?: IconOptions) {
    return html`
      <svg
        slot=${ifDefined(options?.slot)}
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="9"
        viewBox="0 0 14 9"
        fill="none"
      >
        <defs>
          <filter id="shadow" x="-50%" y="-106%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
            <feOffset dx="0" dy="0" result="offsetblur" />
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.8" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path d="M7 1L13 7H1L7 1Z" fill="#FFFFFF" filter="url(#shadow)" />
      </svg>
    `;
  }

  /**
   * Returns a lit `TemplateResult` for a chevronDown SVG icon.
   * The 'slot' attribute is added to the SVG element if the 'slot' parameter is provided.
   *
   * @param options - An object containing optional properties for the SVG element.
   * @returns A lit `TemplateResult` for the chevronDown SVG icon.
   */
  static chevronDown(options?: IconOptions) {
    return html`
      <svg
        slot=${ifDefined(options?.slot)}
        class=${ifDefined(options?.customClass)}
        width="10"
        height="8"
        viewBox="0 0 10 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M5 7.5L6.55671e-07 -8.74228e-07L10 0L5 7.5Z" fill="#0063AD" />
      </svg>
    `;
  }

  /**
   * Returns a lit `TemplateResult` for a chevronRight SVG icon.
   * The 'slot' attribute is added to the SVG element if the 'slot' parameter is provided.
   *
   * @param options - An object containing optional properties for the SVG element.
   * @returns A lit `TemplateResult` for the chevronDown SVG icon.
   */
  static chevronRight(options?: IconOptions) {
    return html`
      <svg
        slot=${ifDefined(options?.slot)}
        class=${ifDefined(options?.customClass)}
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 8L14 12L10 16"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    `;
  }

  /**
   * Returns a lit `TemplateResult` for a sortArrow SVG icon.
   * The 'slot' attribute is added to the SVG element if the 'slot' parameter is provided.
   *
   * @param options - An object containing optional properties for the SVG element.
   * @returns A lit `TemplateResult` for the sortArrow SVG icon.
   */
  static sortArrow(options?: IconOptions) {
    return html`
      <svg
        slot=${ifDefined(options?.slot)}
        class=${ifDefined(options?.customClass)}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M8 3L12 8H8.5L8.5 13H7.5L7.5 8H4L8 3Z"
          fill="#0063AD"
        />
      </svg>
    `;
  }

  /**
   * Returns a lit `TemplateResult` for a tableView SVG icon.
   * The 'slot' attribute is added to the SVG element if the 'slot' parameter is provided.
   *
   * @param options - An object containing optional properties for the SVG element.
   * @returns A lit `TemplateResult` for the tableView SVG icon.
   */
  static tableView(options?: IconOptions) {
    return html`
      <svg
        slot=${ifDefined(options?.slot)}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M14 1.5H2C1.72386 1.5 1.5 1.72386 1.5 2V14C1.5 14.2761 1.72386 14.5 2
          14.5H14C14.2761 14.5 14.5 14.2761 14.5 14V2C14.5 1.72386 14.2761 1.5 14
          1.5ZM2 0.5C1.17157 0.5 0.5 1.17157 0.5 2V14C0.5 14.8284 1.17157 15.5 2 15.5H14C14.8284 15.5 15.5
          14.8284 15.5 14V2C15.5 1.17157 14.8284 0.5 14 0.5H2Z"
          fill="#0063AD"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M0.5 4.79999C0.5 4.52385 0.723858 4.29999 1 4.29999H15C15.2761
          4.29999 15.5 4.52385 15.5 4.79999C15.5 5.07613 15.2761 5.29999 15
          5.29999H1C0.723858 5.29999 0.5 5.07613 0.5 4.79999Z"
          fill="#0063AD"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M5.5 7.79999L15.5 7.79999L15.5 8.79999L5.5 8.79999L5.5 7.79999Z"
          fill="#0063AD"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M4.90039 15.3L4.90039 5.29999H5.90039L5.90039 15.3H4.90039Z"
          fill="#0063AD"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M5.5 11L15.5 11L15.5 12L5.5 12L5.5 11Z"
          fill="#0063AD"
        />
      </svg>
    `;
  }

  /**
   * Returns a lit `TemplateResult` for a chartView SVG icon.
   * The 'slot' attribute is added to the SVG element if the 'slot' parameter is provided.
   *
   * @param options - An object containing optional properties for the SVG element.
   * @returns A lit `TemplateResult` for the chartView SVG icon.
   */
  static chartView(options?: IconOptions) {
    return html`
      <svg
        slot=${ifDefined(options?.slot)}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M4.7543 10.222C4.9578 10.3963 4.98147 10.7025 4.80717 10.906L0.925903 15.4368C0.751598
          15.6403 0.445327 15.6639 0.241826 15.4897C0.0383251 15.3154 0.014657 15.0091 0.188962
          14.8057L4.07022 10.2748C4.24453 10.0713 4.5508 10.0477 4.7543 10.222Z"
          fill="#0063AD"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M5.67215 9.80268C5.76275 9.55054 6.04061 9.41958 6.29277 9.51017L9.11654
          10.5245C9.3687 10.6151 9.49968 10.893 9.40908 11.1451C9.31848 11.3972
          9.04062 11.5282 8.78846 11.4376L5.9647 10.4232C5.71253 10.3326 5.58156
          10.0548 5.67215 9.80268Z"
          fill="#0063AD"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M13.9117 7.01516L10.6738 11.1453L9.91016 10.5468L13.148 6.41663L13.9117 7.01516Z"
          fill="#0063AD"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M0.557424 0.0734863C0.825369 0.0734863 1.04258 0.290673 1.04258 0.558586V15.4318C1.04258
          15.6997 0.825369 15.9169 0.557424 15.9169C0.289478 15.9169 0.0722656
          15.6997 0.0722656 15.4318V0.558586C0.0722656 0.290673 0.289478
          0.0734863 0.557424 0.0734863Z"
          fill="#0063AD"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M15.9551 15.4414C15.9551 15.7093 15.7379 15.9265 15.4699 15.9265L0.594979
          15.9265C0.327034 15.9265 0.10982 15.7093 0.10982 15.4414C0.10982 15.1735
          0.327034 14.9563 0.594979 14.9563L15.4699 14.9563C15.7379 14.9563 15.9551
          15.1735 15.9551 15.4414Z"
          fill="#0063AD"
        />
        <path
          d="M6.05082 9.61051C6.05082 10.1463 5.61643 10.5807 5.08047 10.5807C4.54451 10.5807
          4.11013 10.1463 4.11013 9.61051C4.11013 9.07476 4.54451 8.64034 5.08047 8.64034C5.61643
          8.64034 6.05082 9.07476 6.05082 9.61051Z"
          stroke="#0063AD"
          stroke-width="0.970258"
        />
        <path
          d="M10.9121 11.2502C10.9121 11.7859 10.4778 12.2203 9.9418 12.2203C9.40584
          12.2203 8.97146 11.7859 8.97146 11.2502C8.97146 10.7144 9.40584 10.28 9.9418
          10.28C10.4778 10.28 10.9121 10.7144 10.9121 11.2502Z"
          stroke="#0063AD"
          stroke-width="0.970258"
        />
        <path
          d="M14.8399 6.37974C14.8399 6.91549 14.4055 7.34991 13.8695 7.34991C13.3336
          7.34991 12.8992 6.91549 12.8992 6.37974C12.8992 5.84398 13.3336 5.40957
          13.8695 5.40957C14.4055 5.40957 14.8399 5.84398 14.8399 6.37974Z"
          stroke="#0063AD"
          stroke-width="0.970258"
        />
      </svg>
    `;
  }

  /**
   * Returns a lit `TemplateResult` for a reportLink SVG icon.
   * The 'slot' attribute is added to the SVG element if the 'slot' parameter is provided.
   *
   * @param options - An object containing optional properties for the SVG element.
   * @returns A lit `TemplateResult` for the reportLink SVG icon.
   */
  static reportLink(options?: IconOptions) {
    return html`
      <svg
        slot=${ifDefined(options?.slot)}
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label=${ifDefined(options?.ariaLabel)}
        data-metrics-clickable=${ifDefined(
          options?.analytics?.dataMetricsClickable,
        )}
        data-metrics-link-text=${ifDefined(
          options?.analytics?.dataMetricsLinkText,
        )}
        data-metrics-link-dest=${ifDefined(
          options?.analytics?.dataMetricsLinkUrl,
        )}
      >
        <rect
          x="0.5"
          y="0.5"
          width="31"
          height="31"
          rx="15.5"
          stroke="#0063AD"
        />
        <path
          d="
          M8.20166 16.4787V6.4149C8.20166
          5.90962 8.61127 5.5 9.11655
          5.5H17.8904C18.134 5.49933
          18.3678 5.59581 18.54 5.76807L23.4932 10.7213
          C23.6648 10.8928 23.7612 11.1255 23.7613
          11.3681L23.7549 25.6277C23.7549 26.1329
          23.3452 26.5426 22.84 26.5426H18.2655
          "
          stroke="#0063AD"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M18.2656 5.58203V10.0742C18.2656 10.5794 18.6752 10.9891 19.1805 10.9891H23.6726"
          fill="#0063AD"
        />
        <path
          d="M18.2656 5.58203V10.0742C18.2656 10.5794 18.6752 10.9891 19.1805 10.9891H23.6726"
          stroke="#0063AD"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <g clip-path="url(#clip0_4275_96882)" style="fill: none">
          <path
            d="
            M11.8971 19.3242H8.96201C8.80633 19.3242 8.65702
            19.3861 8.54693 19.4961C8.43684 19.6062 8.375 19.7555
            8.375 19.9112V25.7813C8.375 25.937 8.43684 26.0863
            8.54693 26.1964C8.65702 26.3065 8.80633 26.3683
            8.96201 26.3683H14.8321C14.9878 26.3683
            15.1371 26.3065 15.2472 26.1964C15.3573 26.0863 15.4191 25.937 15.4191
            25.7813V22.8463
            "
            stroke="#0063AD"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M10.1357 24.6179L17.1799 17.5664"
            stroke="#0063AD"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M13.6582 17.5664H17.1803V21.0921"
            stroke="#0063AD"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </g>
        <defs>
          <clipPath id="clip0_4275_96882">
            <rect
              width="10.0638"
              height="10.0638"
              fill="white"
              transform="translate(7.74512 16.9355)"
            />
          </clipPath>
        </defs>
      </svg>
    `;
  }

  /**
   * Returns a string for the stock avatar picture as a data URL.
   *
   * @returns A string for the stock avatar picture SVG icon as a data URL.
   */
  static stockAvatarDataURL() {
    return (
      `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' ` +
      `xmlns:xlink='http://www.w3.org/1999/xlink' viewBox='0, 0, 400,400'` +
      `%3E%3Cg id='svgg'%3E%3Cpath id='path0' d='M0.028 167.300 C 0.044 ` +
      `259.630,0.210 334.958,0.400 335.400 C 0.667 336.021,0.761 335.797,0.819 334.400 C 0.886 332.802,0.924 332.735,1.156 333.800 ` +
      `C 1.468 335.234,1.722 334.893,2.424 332.099 C 2.714 330.944,3.067 330.186,3.209 330.415 C 3.351 330.644,3.706 329.249,3.998 ` +
      `327.315 C 4.674 322.847,5.150 321.446,5.600 322.600 C 5.945 323.486,6.800 321.957,6.800 320.454 C 6.800 319.546,8.854 ` +
      `316.386,9.275 316.646 C 9.454 316.757,9.600 316.594,9.600 316.285 C 9.600 315.669,15.982 309.562,18.714 307.564 C 22.724 ` +
      `304.631,43.307 294.124,48.877 292.167 C 51.009 291.417,54.429 290.065,56.477 289.162 C 58.525 288.258,61.460 287.065,63.000 ` +
      `286.510 C 64.540 285.955,67.510 284.690,69.600 283.699 C 71.690 282.708,76.550 280.764,80.400 279.380 C 91.368 275.438,98.245 ` +
      `272.721,102.800 270.531 C 105.110 269.420,107.990 268.191,109.200 267.799 C 110.410 267.407,113.380 266.094,115.800 264.881 C ` +
      `118.220 263.667,122.270 261.913,124.800 260.983 C 139.254 255.669,147.518 250.404,148.209 246.071 C 148.338 245.262,148.614 ` +
      `244.287,148.822 243.905 C 149.305 243.018,149.324 217.883,148.843 216.228` +
      ` C 147.598 211.947,146.903 210.404,144.424 206.411 C 139.952 199.207,137.335 193.596,135.966 188.276 C 135.298 185.677,134.387 182.707,133.943` +
      ` 181.676 C 133.498 180.644,132.333 177.460,131.352 174.600 C 128.869 167.357,127.847 166.039,122.666 163.385 C 115.115 159.519,111.400 151.015,111.400 137.600` +
      ` C 111.400 124.723,111.949 122.837,117.623 116.200 C 122.688 110.275,123.693 104.918,121.976 93.000 C 120.239 80.942,121.851 61.866,125.257` +
      ` 54.163 C 132.228 38.398,147.202 24.341,164.912 16.935 C 167.821 15.719,171.248 14.278,172.529 13.734 C 181.599` +
      ` 9.881,217.042 9.638,226.000 13.368 C 227.430 13.963,231.223 15.505,234.429 16.793 C 256.592 25.701,273.590 44.561,277.156 64.200 C 279.092 74.857,279.141` +
      ` 77.120,277.812 94.000 C 276.717 107.891,276.992 109.202,282.587 116.800 C 287.739 123.795,288.630` +
      ` 126.273,289.057 134.800 C 289.716 147.936,285.830 157.015,277.039 162.880 C 270.654 167.141,267.671 172.537,264.595 185.394 C 263.119 191.563,258.182` +
      ` 202.525,254.625 207.533 C 251.128 212.456,250.801 214.436,250.800 230.681 C 250.800 237.766,250.962` +
      ` 243.976,251.160 244.481 C 251.358 244.987,251.732 246.188,251.991 247.150 C 252.923 250.610,256.304 253.290,262.800 255.717 C 264.230 256.252,266.750 257.370,268.400` +
      ` 258.203 C 270.050 259.036,273.650 260.482,276.400 261.417 C 279.150 262.352,284.100 264.413,287.400` +
      ` 265.998 C 290.700 267.582,295.200 269.742,297.400 270.797 C 299.600 271.853,303.107 273.302,305.193 274.019 C 307.279 274.735,310.609` +
      ` 276.078,312.593 277.003 C 314.577 277.928,318.000 279.268,320.200 279.979 C 322.400 280.691,326.090 282.148,328.400 283.217 C 330.710 284.286,334.192` +
      ` 285.720,336.137 286.405 C 338.083 287.089,341.143 288.367,342.937 289.244 C 344.732 290.121,348.270 291.483,350.800 292.271 C 355.311 293.676,366.000` +
      ` 298.703,370.263 301.425 C 371.398 302.150,374.098 303.681,376.263 304.828 C 382.579 308.172,390.400 315.074,390.400 317.303 C 390.400 317.712,390.580` +
      ` 317.936,390.800 317.800 C 391.020 317.664,391.200 317.824,391.200 318.157 C 391.200 318.489,391.429 318.904,391.708 319.080 C 392.284 319.442,393.200` +
      ` 321.354,393.200 322.193 C 393.200 323.594,394.005 324.894,394.578 324.418 C 395.048 324.028,395.186 324.275,395.396 325.875 C 395.535 326.934,395.817` +
      ` 328.213,396.024 328.719 C 396.231 329.224,396.400 330.270,396.400 331.042 C 396.400 331.815,396.565 332.345,396.768 332.220 C 396.970 332.095,397.274` +
      ` 333.209,397.444 334.696 C 397.702 336.953,397.799 337.202,398.029 336.200 L 398.305 335.000 398.400 336.200 L 398.495 337.400 398.800 336.200` +
      ` C 399.011 335.372,399.140 336.303,399.216 339.200 L 399.327 343.400 399.621 339.800 C 399.783 337.820,399.934 260.555,399.958 168.100 L 400.000` +
      ` 0.000 200.000 0.000 L 0.000 0.000 0.028 167.300 '` +
      ` stroke='none' fill='%23fcfcfc' fill-rule='evenodd'/%3E%3Cpath id='path1'` +
      ` d='M184.400 11.230 C 179.174 11.736,175.329 12.545,172.549 13.726 C 171.257 14.275,167.821 15.719,164.912 16.935 C 147.202 24.341,132.228 38.398,125.257` +
      ` 54.163 C 121.851 61.866,120.239 80.942,121.976 93.000 C 123.693 104.918,122.688 110.275,117.623 116.200 C 111.949 122.837,111.400 124.723,111.400 137.600` +
      ` C 111.400 151.015,115.115 159.519,122.666 163.385 C 127.847 166.039,128.869 167.357,131.352 174.600 C 132.333 177.460,133.498 180.644,133.943 181.676` +
      ` C 134.387 182.707,135.298 185.677,135.966 188.276 C 137.335 193.596,139.952 199.207,144.424 206.411 C 146.903 210.404,147.598 211.947,148.843 216.228` +
      ` C 149.324 217.883,149.305 243.018,148.822 243.905 C 148.614 244.287,148.338 245.262,148.209 246.071 C 147.518 250.404,139.254 255.669,124.800 260.983` +
      ` C 122.270 261.913,118.220 263.667,115.800 264.881 C 113.380 266.094,110.410 267.407,109.200 267.799 C 107.990 268.191,105.110 269.420,102.800 270.531` +
      ` C 98.245 272.721,91.368 275.438,80.400 279.380 C 76.550 280.764,71.690 282.708,69.600 283.699 C 67.510 284.690,64.540 285.955,63.000 286.510 C 61.460` +
      ` 287.065,58.525 288.258,56.477 289.162 C 54.429 290.065,51.009 291.417,48.877 292.167 C 43.307 294.124,22.724 304.631,18.714 307.564 C 15.982 309.562,9.600` +
      ` 315.669,9.600 316.285 C 9.600 316.594,9.454 316.757,9.275 316.646 C 8.854 316.386,6.800 319.546,6.800 320.454 C 6.800 321.957,5.945 323.486,5.600 322.600` +
      ` C 5.150 321.446,4.674 322.847,3.998 327.315 C 3.706 329.249,3.351 330.644,3.209 330.415 C 3.067 330.186,2.714 330.944,2.424 332.099 C 1.722 334.893,1.468` +
      ` 335.234,1.156 333.800 C 0.924 332.735,0.886 332.802,0.819 334.400 C 0.761 335.797,0.667 336.021,0.400 335.400 C 0.204 334.945,0.044 348.684,0.028 367.300` +
      ` L 0.000 400.000 200.000 400.000 L 400.000` +
      ` 400.000 399.927 368.500 C 399.886 351.175,399.739 338.440,399.600 340.200 L 399.347 343.400 399.226 339.200 C 399.142 336.288,399.012 335.368,398.800 336.200` +
      ` L 398.495 337.400 398.400 336.200 L 398.305` +
      ` 335.000 398.029 336.200 C 397.799 337.202,397.702 336.953,397.444 334.696 C 397.274 333.209,396.970 332.095,396.768 332.220 C 396.565 332.345,396.400` +
      ` 331.815,396.400 331.042 C 396.400 330.270,396.231 329.224,396.024 328.719 C 395.817 328.213,395.535 326.934,395.396 325.875 C 395.186 324.275,395.048` +
      ` 324.028,394.578 324.418 C 394.005 324.894,393.200 323.594,393.200 322.193 C 393.200 321.354,392.284 319.442,391.708 319.080 C 391.429 318.904,391.200` +
      ` 318.489,391.200 318.157 C 391.200 317.824,391.020 317.664,390.800 317.800 C 390.580 317.936,390.400 317.712,390.400 317.303 C 390.400 315.074,382.579` +
      ` 308.172,376.263 304.828 C 374.098 303.681,371.398 302.150,370.263 301.425 C 366.000 298.703,355.311 293.676,350.800 292.271 C 348.270 291.483,344.732` +
      ` 290.121,342.937 289.244 C 341.143 288.367,338.083 287.089,336.137 286.405 C 334.192 285.720,330.710 284.286,328.400 283.217 C 326.090 282.148,322.400` +
      ` 280.691,320.200 279.979 C 318.000 279.268,314.577 277.928,312.593 277.003 C 310.609 276.078,307.279 274.735,305.193 274.019 C 303.107 273.302,299.600` +
      ` 271.853,297.400 270.797 C 295.200 269.742,290.700 267.582,287.400 265.998 C 284.100 264.413,279.150 262.352,276.400 261.417 C 273.650 260.482,270.050` +
      ` 259.036,268.400 258.203 C 266.750 257.370,264.230 256.252,262.800 255.717 C 256.304 253.290,252.923 250.610,251.991 247.150 C 251.732 246.188,251.358` +
      ` 244.987,251.160 244.481 C 250.962 243.976,250.800 237.766,250.800 230.681 C 250.801 214.436,251.128 212.456,254.625 207.533 C 258.182 202.525,263.119` +
      ` 191.563,264.595 185.394 C 267.671 172.537,270.654 167.141,277.039 162.880 C 285.830 157.015,289.716 147.936,289.057 134.800 C 288.630 126.273,287.739` +
      ` 123.795,282.587 116.800 C 276.992 109.202,276.717 107.891,277.812 94.000 C` +
      ` 279.141 77.120,279.092 74.857,277.156 64.200 C 273.590 44.561,256.592 25.701,234.429 16.793 C 231.223 15.505,227.430 13.963,226.000 13.368 C 220.948` +
      ` 11.264,196.849 10.026,184.400 11.230 '` +
      ` stroke='none' fill='%23ececec' fill-rule='evenodd'/%3E%3C/g%3E%3C/svg%3E`
    );
  }

  /**
   * Returns a lit-html template for the stock avatar picture SVG icon.
   * The 'slot' attribute is added to the SVG element if the 'slot' parameter is provided.
   *
   * @param options - An object containing optional properties for the SVG element.
   * @returns A lit-html template for the stock avatar picture SVG icon.
   */
  static stockAvatar(options?: IconOptions) {
    return html`
      <svg
        slot=${ifDefined(options?.slot)}
        xmlns="http://www.w3.org/2000/svg"
        xmlns:xlink="http://www.w3.org/1999/xlink"
        viewBox="0, 0, 400,400"
      >
        <g id="svgg">
          <path
            id="path0"
            d="M0.028 167.300 C 0.044 259.630,0.210 334.958,0.400 335.400 C 0.667 336.021,0.761 335.797,0.819 334.400 C 0.886 332.802,0.924
              332.735,1.156 333.800 C 1.468 335.234,1.722 334.893,2.424 332.099 C 2.714 330.944,3.067 330.186,3.209 330.415 C 3.351 330.644,3.706
              329.249,3.998 327.315 C 4.674 322.847,5.150 321.446,5.600 322.600 C 5.945 323.486,6.800 321.957,6.800 320.454 C 6.800 319.546,8.854
              316.386,9.275 316.646 C 9.454 316.757,9.600 316.594,9.600 316.285 C 9.600 315.669,15.982 309.562,18.714 307.564 C 22.724 304.631,43.307
              294.124,48.877 292.167 C 51.009 291.417,54.429 290.065,56.477 289.162 C 58.525 288.258,61.460 287.065,63.000 286.510 C 64.540
              285.955,67.510 284.690,69.600 283.699 C 71.690 282.708,76.550 280.764,80.400 279.380 C 91.368 275.438,98.245 272.721,102.800
              270.531 C 105.110 269.420,107.990 268.191,109.200 267.799 C 110.410 267.407,113.380 266.094,115.800 264.881 C 118.220 263.667,122.270
              261.913,124.800 260.983 C 139.254 255.669,147.518 250.404,148.209 246.071 C 148.338 245.262,148.614 244.287,148.822 243.905 C 149.305
              243.018,149.324 217.883,148.843 216.228 C 147.598 211.947,146.903 210.404,144.424 206.411 C 139.952 199.207,137.335 193.596,135.966
              188.276 C 135.298 185.677,134.387 182.707,133.943 181.676 C 133.498 180.644,132.333 177.460,131.352 174.600 C 128.869 167.357,127.847
              166.039,122.666 163.385 C 115.115 159.519,111.400 151.015,111.400 137.600 C 111.400 124.723,111.949 122.837,117.623 116.200 C 122.688
              110.275,123.693 104.918,121.976 93.000 C 120.239 80.942,121.851 61.866,125.257 54.163 C 132.228 38.398,147.202 24.341,164.912 16.935
              C 167.821 15.719,171.248 14.278,172.529 13.734 C 181.599 9.881,217.042 9.638,226.000 13.368 C 227.430 13.963,231.223 15.505,234.429
              16.793 C 256.592 25.701,273.590 44.561,277.156 64.200 C 279.092 74.857,279.141 77.120,277.812 94.000 C 276.717 107.891,276.992
              109.202,282.587 116.800 C 287.739 123.795,288.630 126.273,289.057 134.800 C 289.716 147.936,285.830 157.015,277.039 162.880 C 270.654
              167.141,267.671 172.537,264.595 185.394 C 263.119 191.563,258.182 202.525,254.625 207.533 C 251.128 212.456,250.801 214.436,250.800 230.681
              C 250.800 237.766,250.962 243.976,251.160 244.481 C 251.358 244.987,251.732 246.188,251.991 247.150 C 252.923 250.610,256.304 253.290,262.800
              255.717 C 264.230 256.252,266.750 257.370,268.400 258.203 C 270.050 259.036,273.650 260.482,276.400 261.417 C 279.150 262.352,284.100
              264.413,287.400 265.998 C 290.700 267.582,295.200 269.742,297.400 270.797 C 299.600 271.853,303.107 273.302,305.193 274.019 C 307.279
              274.735,310.609 276.078,312.593 277.003 C 314.577 277.928,318.000 279.268,320.200 279.979 C 322.400 280.691,326.090 282.148,328.400 283.217
              C 330.710 284.286,334.192 285.720,336.137 286.405 C 338.083 287.089,341.143 288.367,342.937 289.244 C 344.732 290.121,348.270 291.483,350.800
              292.271 C 355.311 293.676,366.000 298.703,370.263 301.425 C 371.398 302.150,374.098 303.681,376.263 304.828 C 382.579 308.172,390.400
              315.074,390.400 317.303 C 390.400 317.712,390.580 317.936,390.800 317.800 C 391.020 317.664,391.200 317.824,391.200 318.157 C 391.200
              318.489,391.429 318.904,391.708 319.080 C 392.284 319.442,393.200 321.354,393.200 322.193 C 393.200 323.594,394.005 324.894,394.578 324.418 C
              395.048 324.028,395.186 324.275,395.396 325.875 C 395.535 326.934,395.817 328.213,396.024 328.719 C 396.231 329.224,396.400 330.270,396.400 331.042
              C 396.400 331.815,396.565 332.345,396.768 332.220 C 396.970 332.095,397.274 333.209,397.444 334.696 C 397.702 336.953,397.799 337.202,398.029
              336.200 L 398.305 335.000 398.400 336.200 L 398.495 337.400 398.800 336.200 C 399.011 335.372,399.140 336.303,399.216 339.200 L 399.327 343.400
              399.621 339.800 C 399.783 337.820,399.934 260.555,399.958 168.100 L 400.000 0.000 200.000 0.000 L 0.000 0.000 0.028 167.300 "
            stroke="none"
            fill="#fcfcfc"
            fill-rule="evenodd"
          />
          <path
            id="path1"
            d="M184.400 11.230 C 179.174 11.736,175.329 12.545,172.549 13.726 C 171.257 14.275,167.821 15.719,164.912 16.935 C 147.202 24.341,132.228
              38.398,125.257 54.163 C 121.851 61.866,120.239 80.942,121.976 93.000 C 123.693 104.918,122.688 110.275,117.623 116.200 C 111.949 122.837,111.400
              124.723,111.400 137.600 C 111.400 151.015,115.115 159.519,122.666 163.385 C 127.847 166.039,128.869 167.357,131.352 174.600 C 132.333
              177.460,133.498 180.644,133.943 181.676 C 134.387 182.707,135.298 185.677,135.966 188.276 C 137.335 193.596,139.952 199.207,144.424 206.411
              C 146.903 210.404,147.598 211.947,148.843 216.228 C 149.324 217.883,149.305 243.018,148.822 243.905 C 148.614 244.287,148.338 245.262,148.209
              246.071 C 147.518 250.404,139.254 255.669,124.800 260.983 C 122.270 261.913,118.220 263.667,115.800 264.881 C 113.380 266.094,110.410
              267.407,109.200 267.799 C 107.990 268.191,105.110 269.420,102.800 270.531 C 98.245 272.721,91.368 275.438,80.400 279.380 C 76.550 280.764,71.690
              282.708,69.600 283.699 C 67.510 284.690,64.540 285.955,63.000 286.510 C 61.460 287.065,58.525 288.258,56.477 289.162 C 54.429 290.065,51.009
              291.417,48.877 292.167 C 43.307 294.124,22.724 304.631,18.714 307.564 C 15.982 309.562,9.600 315.669,9.600 316.285 C 9.600 316.594,9.454
              316.757,9.275 316.646 C 8.854 316.386,6.800 319.546,6.800 320.454 C 6.800 321.957,5.945 323.486,5.600 322.600 C 5.150 321.446,4.674 322.847,3.998
              327.315 C 3.706 329.249,3.351 330.644,3.209 330.415 C 3.067 330.186,2.714 330.944,2.424 332.099 C 1.722 334.893,1.468 335.234,1.156 333.800
              C 0.924 332.735,0.886 332.802,0.819 334.400 C 0.761 335.797,0.667 336.021,0.400 335.400 C 0.204 334.945,0.044 348.684,0.028 367.300 L 0.000
              400.000 200.000 400.000 L 400.000 400.000 399.927 368.500 C 399.886 351.175,399.739 338.440,399.600 340.200 L 399.347 343.400 399.226 339.200
              C 399.142 336.288,399.012 335.368,398.800 336.200 L 398.495 337.400 398.400 336.200 L 398.305 335.000 398.029 336.200 C 397.799 337.202,397.702
              336.953,397.444 334.696 C 397.274 333.209,396.970 332.095,396.768 332.220 C 396.565 332.345,396.400 331.815,396.400 331.042 C 396.400 330.270,396.231
              329.224,396.024 328.719 C 395.817 328.213,395.535 326.934,395.396 325.875 C 395.186 324.275,395.048 324.028,394.578 324.418 C 394.005
              324.894,393.200 323.594,393.200 322.193 C 393.200 321.354,392.284 319.442,391.708 319.080 C 391.429 318.904,391.200 318.489,391.200 318.157
              C 391.200 317.824,391.020 317.664,390.800 317.800 C 390.580 317.936,390.400 317.712,390.400 317.303 C 390.400 315.074,382.579 308.172,376.263
              304.828 C 374.098 303.681,371.398 302.150,370.263 301.425 C 366.000 298.703,355.311 293.676,350.800 292.271 C 348.270 291.483,344.732
              290.121,342.937 289.244 C 341.143 288.367,338.083 287.089,336.137 286.405 C 334.192 285.720,330.710 284.286,328.400 283.217 C 326.090
              282.148,322.400 280.691,320.200 279.979 C 318.000 279.268,314.577 277.928,312.593 277.003 C 310.609 276.078,307.279 274.735,305.193 274.019
              C 303.107 273.302,299.600 271.853,297.400 270.797 C 295.200 269.742,290.700 267.582,287.400 265.998 C 284.100 264.413,279.150 262.352,276.400
              261.417 C 273.650 260.482,270.050 259.036,268.400 258.203 C 266.750 257.370,264.230 256.252,262.800 255.717 C 256.304 253.290,252.923
              250.610,251.991 247.150 C 251.732 246.188,251.358 244.987,251.160 244.481 C 250.962 243.976,250.800 237.766,250.800 230.681 C 250.801
              214.436,251.128 212.456,254.625 207.533 C 258.182 202.525,263.119 191.563,264.595 185.394 C 267.671 172.537,270.654 167.141,277.039 162.880
              C 285.830 157.015,289.716 147.936,289.057 134.800 C 288.630 126.273,287.739 123.795,282.587 116.800 C 276.992 109.202,276.717 107.891,277.812
              94.000 C 279.141 77.120,279.092 74.857,277.156 64.200 C 273.590 44.561,256.592 25.701,234.429 16.793 C 231.223 15.505,227.430 13.963,226.000
              13.368 C 220.948 11.264,196.849 10.026,184.400 11.230 "
            stroke="none"
            fill="#ececec"
            fill-rule="evenodd"
          />
        </g>
      </svg>
    `;
  }

  static sortedAscending(options?: IconOptions) {
    return html`<svg
      slot=${ifDefined(options?.slot)}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M8 3L12 8H8.5L8.5 13H7.5L7.5 8H4L8 3Z"
      />
    </svg> `;
  }

  static sortedDescending(options?: IconOptions) {
    return html`<svg
      slot=${ifDefined(options?.slot)}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M8.5 8L8.5 3H7.5L7.5 8H4L8 13L12 8H8.5Z"
      />
    </svg>`;
  }

  /**
   * Returns a lit `TemplateResult` for a Close SVG icon.
   * The 'slot' attribute is added to the SVG element if the 'slot' parameter is provided.
   *
   * @param options - An object containing optional properties for the SVG element.
   * @returns A lit `TemplateResult` for the Close SVG icon.
   */
  static close(options?: IconOptions) {
    return html`
      <svg
        slot=${ifDefined(options?.slot)}
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label=${ifDefined(options?.ariaLabel)}
        data-metrics-clickable=${ifDefined(
          options?.analytics?.dataMetricsClickable,
        )}
        data-metrics-type=${ifDefined(options?.analytics?.dataMetricsType)}
        data-metrics-link-text=${ifDefined(
          options?.analytics?.dataMetricsLinkText,
        )}
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="
          M10.3133 11.0201C10.5085 11.2154
          10.8251 11.2154 11.0204 11.0201C11.2156 10.8249
          11.2156 10.5083 11.0204 10.313L6.70727
          5.99992L11.0204 1.68681C11.2156 1.49154
          11.2156 1.17496 11.0204 0.9797C10.8251
          0.784438 10.5085 0.784438 10.3133 0.9797L6.00016
          5.29281L1.68705 0.979699C1.49179 0.784436 1.17521
          0.784437 0.979943 0.979699C0.784681 1.17496 0.78468
          1.49154 0.979943 1.68681L5.29306 5.99992L0.979943
          10.313C0.78468 10.5083 0.784681 10.8249 0.979943
          11.0201C1.17521 11.2154 1.49179 11.2154 1.68705
          11.0201L6.00016 6.70703L10.3133 11.0201Z"
          fill="#0063AD"
        />
      </svg>
    `;
  }

  /**
   * Returns a lit `TemplateResult` for a Circular Close SVG icon.
   * The 'slot' attribute is added to the SVG element if the 'slot' parameter is provided.
   *
   * @param options - An object containing optional properties for the SVG element.
   * @returns A lit `TemplateResult` for the Circular Close SVG icon.
   */
  static circularClose(
    beaconColorToken = '--beacon-system-color-icon-tertiary',
    options?: IconOptions,
  ) {
    return html`
      <svg
        slot=${ifDefined(options?.slot)}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="0.5"
          y="0.5"
          width="15"
          height="15"
          rx="7.5"
          fill="white"
          stroke="var(${beaconColorToken})"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="
          M8.00019 8.70952L4.70733 12.0045L4 11.2976L7.29338
          8.00211L4 4.70664L4.70733 3.99976L8.00021 7.29473L11.2931
          3.99978L12.0004 4.70666L8.70702 8.00214L12.0004 11.2976L11.2931
          12.0045L8.00019 8.70952Z"
          fill="var(${beaconColorToken})"
        />
      </svg>
    `;
  }

  /**
   * Returns a lit `TemplateResult` for a morningstar medalist rating SVG icon.
   * The 'slot' attribute is added to the SVG element if the 'slot' parameter is provided.
   * The 'type' parameter is used to determine the color of the medal.
   * If an incorrect 'type' parameter is not provided, returns an empty result.
   *
   * @param type - The type of Morningstar medalist rating.
   * @param options - An object containing optional properties for the SVG element.
   * @returns A lit `TemplateResult` for the download SVG icon.
   */
  static morningstarMedalistRating(
    type: MorningstarMedalistRating,
    options?: IconOptions,
  ) {
    if (type === MorningstarMedalistRating.BRONZE) {
      return html`
        <svg
          slot=${ifDefined(options?.slot)}
          version="1.1"
          id="Layer_1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          x="0px"
          y="0px"
          viewBox="0 0 86.6 52"
          style="enable-background:new 0 0 86.6 52;"
          xml:space="preserve"
          aria-label=${ifDefined(options?.ariaLabel)}
        >
          <style type="text/css">
            .st0 {
              fill: #ffffff;
            }
            .st1 {
              opacity: 0.15;
            }
            .st2 {
              opacity: 0.25;
            }
            .st3 {
              fill: #c8745a;
            }
            .st4 {
              fill: url(#SVGID_1_);
            }
            .st5 {
              fill: url(#SVGID_00000134943190448495946280000003242434843332248997_);
            }
            .st6 {
              fill: url(#SVGID_00000161609437215912179470000011578718133998799286_);
            }
            .st7 {
              fill: url(#SVGID_00000115475309721896548980000007383500191327836567_);
            }
            .st8 {
              fill: url(#SVGID_00000133499745826185358760000006707881142222399617_);
            }
            .st9 {
              fill: url(#SVGID_00000090282644259635658300000012603923284793133968_);
            }
            .st10 {
              fill: url(#SVGID_00000045594492309527066600000000605362268213617848_);
            }
            .st11 {
              fill: url(#SVGID_00000065070341881876123360000007319741381264831876_);
            }
            .st12 {
              fill: url(#SVGID_00000183948593688881288880000008478788771339540648_);
            }
            .st13 {
              fill: url(#SVGID_00000166635372096802103610000000482060152715575473_);
            }
            .st14 {
              fill: url(#SVGID_00000087399235759807370250000010112322450007712190_);
            }
            .st15 {
              fill: url(#SVGID_00000166656943702135164090000006560964488499174583_);
            }
            .st16 {
              fill: url(#SVGID_00000136377852530217443930000003093176395887271578_);
            }
            .st17 {
              fill: url(#SVGID_00000027574442025813445370000007619516744850083235_);
            }
            .st18 {
              opacity: 0.45;
            }
            .st19 {
              fill: #774331;
            }
            .st20 {
              opacity: 0.15;
              enable-background: new;
            }
            .st21 {
              opacity: 0.25;
              enable-background: new;
            }
            .st22 {
              fill: #faa41d;
            }
            .st23 {
              fill: url(#SVGID_00000162335598540364641340000017545291334957340070_);
            }
            .st24 {
              fill: url(#SVGID_00000175320473044564265020000006088444384789165707_);
            }
            .st25 {
              fill: url(#SVGID_00000003801554230982211800000005904445384359002806_);
            }
            .st26 {
              fill: url(#SVGID_00000127002047165914583800000017357725673091366043_);
            }
            .st27 {
              fill: url(#SVGID_00000059292670638234851190000016350873340625861279_);
            }
            .st28 {
              fill: url(#SVGID_00000007399961095644802480000013442338066524068242_);
            }
            .st29 {
              fill: url(#SVGID_00000064331903985551816960000009146836545784058776_);
            }
            .st30 {
              fill: url(#SVGID_00000101801031474716205390000012744723272847723918_);
            }
            .st31 {
              fill: url(#SVGID_00000126293184768791807710000002539346012025544365_);
            }
            .st32 {
              fill: url(#SVGID_00000150070112445091154290000006315064166687779504_);
            }
            .st33 {
              fill: url(#SVGID_00000078759213596664781850000016957653078955661736_);
            }
            .st34 {
              fill: url(#SVGID_00000178163370380355197230000005526738808280743859_);
            }
            .st35 {
              opacity: 0.45;
              fill: #915f02;
              enable-background: new;
            }
            .st36 {
              opacity: 0.25;
              fill: #915f02;
              enable-background: new;
            }
            .st37 {
              fill: #8b9b97;
            }
            .st38 {
              fill: url(#SVGID_00000143594510539805741970000014152821041369381264_);
            }
            .st39 {
              fill: url(#SVGID_00000099660319414968001820000018405010808969693088_);
            }
            .st40 {
              fill: url(#SVGID_00000069395105068069316030000006148215732663231362_);
            }
            .st41 {
              fill: url(#SVGID_00000077321921716325888320000016890664773815835537_);
            }
            .st42 {
              fill: url(#SVGID_00000030474573905492120870000015015495395011962810_);
            }
            .st43 {
              fill: url(#SVGID_00000085215283670739539260000012864113563653603000_);
            }
            .st44 {
              fill: url(#SVGID_00000151519846000394275440000007569939970537654441_);
            }
            .st45 {
              fill: url(#SVGID_00000178205279894379673380000000387082860355299506_);
            }
            .st46 {
              fill: url(#SVGID_00000081646976762929477520000011198841434625731210_);
            }
            .st47 {
              fill: url(#SVGID_00000155841201946397303850000006612045613513165240_);
            }
            .st48 {
              fill: url(#SVGID_00000091712279658511054310000011352795043115486868_);
            }
            .st49 {
              fill: url(#SVGID_00000144299075184581564080000015939339267665859214_);
            }
            .st50 {
              fill: url(#SVGID_00000092441700017126508290000004977444621396683956_);
            }
            .st51 {
              fill: url(#SVGID_00000090992374228139618820000011107572445788597926_);
            }
            .st52 {
              fill: url(#SVGID_00000037652112295023274990000011414896550774797479_);
            }
            .st53 {
              opacity: 0.45;
              fill: #596563;
              enable-background: new;
            }
            .st54 {
              opacity: 0.25;
              fill: #596563;
              enable-background: new;
            }
          </style>
          <g>
            <g>
              <g>
                <g>
                  <g>
                    <path
                      class="st0"
                      d="M2.1,1.7v24c0,13.6,9.7,19.6,18.9,23.8c9.2-4.3,18.9-10.3,18.9-23.8v-24L2.1,1.7z"
                    />
                  </g>
                </g>
              </g>
            </g>
            <g>
              <g class="st1">
                <g>
                  <g>
                    <path
                      d="M66.8,23.7c0-0.5,0.5-1,1-1s1,0.5,1,1v9H71v-9c0-1.3-1-2.2-2.2-2.2c-0.8,0-1.5,0.5-1.9,1v-0.9h-2.1v11.1h2.1L66.8,23.7
                  z"
                    />
                    <path
                      d="M54.5,25.5c0-1.7,1.1-1.7,1.7-1.7h0.2v-2h-0.2c-0.5,0-1.3,0-1.6,1.2h-0.1v-1.1h-2.1v10.9h2.1L54.5,25.5z"
                    />
                    <path
                      d="M63.2,30c0,1.6-1.4,3-3,3l0,0c-1.6,0-3-1.4-3-3v-5.5c0-1.6,1.4-3,3-3s3,1.4,3,3V30z M61.1,24c0-0.5-0.5-1-1-1
                  s-0.9,0.5-0.9,1v6.5c0,0.5,0.5,1,0.9,1c0.5,0,1-0.5,1-1V24z"
                    />
                    <path
                      d="M81.4,33c-1.6,0-3-1.4-3-3v-5.5c0-1.6,1.4-3,3-3s3,1.4,3,3V28h-4v2.5c0,0.5,0.5,1,1,1s1-0.5,1-1V29h2.1v1
                  C84.4,31.6,83,33,81.4,33L81.4,33z M80.5,26.5h1.9V24c0-0.5-0.5-1-1-1s-1,0.5-1,1v2L80.5,26.5L80.5,26.5z"
                    />
                    <polygon
                      points="77.3,32.7 72.2,32.7 74.7,23.8 72.5,23.8 72.5,21.7 77.5,21.7 74.9,30.7 77.3,30.7 					"
                    />
                    <path
                      d="M47.5,32.7h-3.6V17.2h3.5c2.3,0,3.4,1.5,3.4,3.4v2.1c0,0.9-0.8,2-2.1,2.1c1.5,0.1,2.2,1.6,2.2,2.5v2.1
                  C50.8,31.3,49.9,32.7,47.5,32.7L47.5,32.7z M45.9,31.1h1.6c0.7,0,1.3-0.5,1.3-1.3v-3c0-0.7-0.5-1.3-1.2-1.3h-0.1h-1.6V31.1
                  L45.9,31.1z M45.9,23.9h1.5h0.1c0.6,0,1.2-0.6,1.2-1.3v-2.5c0-0.7-0.5-1.3-1.3-1.3h-1.5V23.9L45.9,23.9z"
                    />
                  </g>
                </g>
              </g>
              <g class="st1">
                <g>
                  <g>
                    <path
                      d="M66.8,23.7c0-0.5,0.5-1,1-1s1,0.5,1,1v9H71v-9c0-1.3-1-2.2-2.2-2.2c-0.8,0-1.5,0.5-1.9,1v-0.9h-2.1v11.1h2.1L66.8,23.7
                  z"
                    />
                    <path
                      d="M54.5,25.5c0-1.7,1.1-1.7,1.7-1.7h0.2v-2h-0.2c-0.5,0-1.3,0-1.6,1.2h-0.1v-1.1h-2.1v10.9h2.1L54.5,25.5z"
                    />
                    <path
                      d="M63.2,30c0,1.6-1.4,3-3,3l0,0c-1.6,0-3-1.4-3-3v-5.5c0-1.6,1.4-3,3-3s3,1.4,3,3V30z M61.8,24c0-0.5-1.1-1.6-1.6-1.6
                  s-2.3,1.1-2.3,1.6v6.5c0,0.5,1.7,1.6,2.3,1.6s1.6-1.1,1.6-1.6V24z"
                    />
                    <path
                      d="M81.4,33c-1.6,0-3-1.4-3-3v-5.5c0-1.6,1.4-3,3-3s3,1.4,3,3v2.8h-4.6v3.2c0,0.5,1.1,1.6,1.6,1.6S83,31,83,30.5V29h1.4v1
                  C84.4,31.6,83,33,81.4,33L81.4,33z M79.1,27.2H83V24c0-0.5-1.1-1.9-1.6-1.9s-2.3,1.5-2.3,1.9v2.6V27.2z"
                    />
                    <polygon
                      points="77.3,32.7 72.2,32.7 74.7,23.8 72.5,23.8 72.5,21.7 77.5,21.7 74.9,30.7 77.3,30.7 					"
                    />
                    <path
                      d="M47.5,32.7h-3.6V17.2h3.5c2.3,0,3.4,1.5,3.4,3.4v2.1c0,0.9-0.8,2-2.1,2.1c1.5,0.1,2.2,1.6,2.2,2.5v2.1
                  C50.8,31.3,49.9,32.7,47.5,32.7L47.5,32.7z M44.5,31.8h3c0.7,0,2-1.3,2-2v-3c0-0.7-1.2-1.3-1.9-1.3h-0.1h-3V31.8L44.5,31.8z
                  M44.5,24.6h2.9h0.1c0.6,0,1.9-1.3,1.9-2v-2.5c0-0.7-1.3-2-2-2h-2.9V24.6L44.5,24.6z"
                    />
                  </g>
                </g>
              </g>
              <g class="st2">
                <g>
                  <g>
                    <path
                      d="M2.1,1.7v24c0,13.6,9.7,19.6,18.9,23.8c9.2-4.2,19-10.2,19-23.7V1.7H2.1z M24.4,29.3L21,43.7l-3.4-14.4L3.5,33.6
                  l10.7-10.1L3.5,13.4l14.1,4.2l3.5-14.3l3.4,14.3l14.1-4.2L27.8,23.5l10.7,10.1L24.4,29.3z"
                    />
                  </g>
                </g>
              </g>
              <g>
                <polygon
                  class="st3"
                  points="79.5,35.1 80.3,35.1 80.3,37.5 80.6,37.5 80.6,35.1 81.5,35.1 81.5,34.9 79.5,34.9 			"
                />
                <polygon
                  class="st3"
                  points="82.9,37.2 82,34.9 81.7,34.9 81.7,37.5 81.9,37.5 81.9,35.3 81.9,35.3 82.8,37.5 83,37.5 83.9,35.3
              83.9,35.3 83.9,37.5 84.2,37.5 84.2,34.9 83.8,34.9 			"
                />
                <g>
                  <path
                    class="st3"
                    d="M66.8,23.7c0-0.5,0.5-1,1-1s1,0.5,1,1v9H71v-9c0-1.3-1-2.2-2.2-2.2c-0.8,0-1.5,0.5-1.9,1v-0.9h-2.1v11.1
                h2.1L66.8,23.7z"
                  />
                  <path
                    class="st3"
                    d="M54.5,25.5c0-1.7,1.1-1.7,1.7-1.7h0.2v-2h-0.2c-0.5,0-1.3,0-1.6,1.2h-0.1v-1.1h-2.1v10.9h2.1L54.5,25.5z"
                  />
                  <path
                    class="st3"
                    d="M63.2,30c0,1.6-1.4,3-3,3l0,0c-1.6,0-3-1.4-3-3v-5.5c0-1.6,1.4-3,3-3s3,1.4,3,3V30z M61.1,24
                c0-0.5-0.5-1-1-1s-0.9,0.5-0.9,1v6.5c0,0.5,0.5,1,0.9,1c0.5,0,1-0.5,1-1V24z"
                  />
                  <path
                    class="st3"
                    d="M81.4,33c-1.6,0-3-1.4-3-3v-5.5c0-1.6,1.4-3,3-3s3,1.4,3,3V28h-4v2.5c0,0.5,0.5,1,1,1s1-0.5,1-1V29h2.1v1
                C84.4,31.6,83,33,81.4,33L81.4,33z M80.5,26.5h1.9V24c0-0.5-0.5-1-1-1s-1,0.5-1,1v2L80.5,26.5L80.5,26.5z"
                  />
                  <polygon
                    class="st3"
                    points="77.3,32.7 72.2,32.7 74.7,23.8 72.5,23.8 72.5,21.7 77.5,21.7 74.9,30.7 77.3,30.7 				"
                  />
                  <path
                    class="st3"
                    d="M47.5,32.7h-3.6V17.2h3.5c2.3,0,3.4,1.5,3.4,3.4v2.1c0,0.9-0.8,2-2.1,2.1c1.5,0.1,2.2,1.6,2.2,2.5v2.1
                C50.8,31.3,49.9,32.7,47.5,32.7L47.5,32.7z M45.9,31.1h1.6c0.7,0,1.3-0.5,1.3-1.3v-3c0-0.7-0.5-1.3-1.2-1.3h-0.1h-1.6V31.1
                L45.9,31.1z M45.9,23.9h1.5h0.1c0.6,0,1.2-0.6,1.2-1.3v-2.5c0-0.7-0.5-1.3-1.3-1.3h-1.5V23.9L45.9,23.9z"
                  />
                </g>
                <g>
                  <path
                    class="st3"
                    d="M2.1,1.7v24c0,13.6,9.7,19.6,18.9,23.8c9.2-4.2,19-10.2,19-23.7V1.7H2.1z M24.4,29.3L21,43.7l-3.4-14.4
                L3.5,33.6l10.7-10.1L3.5,13.4l14.1,4.2l3.5-14.3l3.4,14.3l14.1-4.2L27.8,23.5l10.7,10.1L24.4,29.3z"
                  />
                </g>
              </g>
              <g>
                <g>
                  <g>
                    <linearGradient
                      id="SVGID_1_"
                      gradientUnits="userSpaceOnUse"
                      x1="-3244.1621"
                      y1="801.1627"
                      x2="-3244.1621"
                      y2="768.296"
                      gradientTransform="matrix(-1 0 0 1 -3223.1621 -739.9971)"
                    >
                      <stop offset="0" style="stop-color:#FFFFFF" />
                      <stop
                        offset="1"
                        style="stop-color:#FFFFFF;stop-opacity:0"
                      />
                    </linearGradient>
                    <path
                      class="st4"
                      d="M2.1,1.7v24c0,13.6,9.7,19.6,18.9,23.8c9.2-4.3,18.9-10.3,18.9-23.8v-24L2.1,1.7z M24.4,29.3L21,43.7
                  l-3.4-14.4L3.5,33.6l10.7-10.1L3.5,13.4l14.1,4.2l3.5-14.3l3.4,14.3l14.1-4.2L27.8,23.5l10.7,10.1L24.4,29.3z"
                    />
                  </g>
                </g>
              </g>
              <g>
                <linearGradient
                  id="SVGID_00000004504584710901135770000007037928704394629299_"
                  gradientUnits="userSpaceOnUse"
                  x1="-20.3925"
                  y1="762.95"
                  x2="50.8943"
                  y2="762.95"
                  gradientTransform="matrix(1 0 0 1 0 -740)"
                >
                  <stop offset="0" style="stop-color:#FFFFFF;stop-opacity:0" />
                  <stop offset="0.5" style="stop-color:#FFFFFF" />
                  <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
                </linearGradient>
                <path
                  style="fill:url(#SVGID_00000004504584710901135770000007037928704394629299_);"
                  d="M52.5,22c0.8,0.7,1.6,1.5,2.4,2.2
              c0-0.1,0.1-0.1,0.1-0.2c-0.8-0.8-1.6-1.5-2.5-2.3h-0.1L52.5,22L52.5,22z"
                />

                <linearGradient
                  id="SVGID_00000000200282741669218110000016799216370864296324_"
                  gradientUnits="userSpaceOnUse"
                  x1="-20.444"
                  y1="767.55"
                  x2="50.8891"
                  y2="767.55"
                  gradientTransform="matrix(1 0 0 1 0 -740)"
                >
                  <stop offset="0" style="stop-color:#FFFFFF;stop-opacity:0" />
                  <stop offset="0.5" style="stop-color:#FFFFFF" />
                  <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
                </linearGradient>
                <path
                  style="fill:url(#SVGID_00000000200282741669218110000016799216370864296324_);"
                  d="M59.2,28.9v-0.4
              c-0.6-0.8-1.4-1.5-2.1-2.3v0.3C57.8,27.4,58.6,28.1,59.2,28.9z"
                />

                <linearGradient
                  id="SVGID_00000113322829646576265380000010108906532256357787_"
                  gradientUnits="userSpaceOnUse"
                  x1="-20.3"
                  y1="771.65"
                  x2="50.9059"
                  y2="771.65"
                  gradientTransform="matrix(1 0 0 1 0 -740)"
                >
                  <stop offset="0" style="stop-color:#FFFFFF;stop-opacity:0" />
                  <stop offset="0.5" style="stop-color:#FFFFFF" />
                  <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
                </linearGradient>
                <path
                  style="fill:url(#SVGID_00000113322829646576265380000010108906532256357787_);"
                  d="M61,31.1c0.4,0.5,0.6,0.9,1,1.3
              c0.1,0,0.1-0.1,0.2-0.1c-0.4-0.5-0.7-0.9-1.1-1.4C61,30.9,61,31,61,31.1z"
                />

                <linearGradient
                  id="SVGID_00000013157731200577940440000014464187119856329360_"
                  gradientUnits="userSpaceOnUse"
                  x1="-20.3809"
                  y1="758.8"
                  x2="50.8939"
                  y2="758.8"
                  gradientTransform="matrix(1 0 0 1 0 -740)"
                >
                  <stop offset="0" style="stop-color:#FFFFFF;stop-opacity:0" />
                  <stop offset="0.5" style="stop-color:#FFFFFF" />
                  <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
                </linearGradient>
                <path
                  style="fill:url(#SVGID_00000013157731200577940440000014464187119856329360_);"
                  d="M50.7,20.5L50.7,20.5c0-0.1,0-0.2,0-0.3
              c-1.4-1.1-2.7-2.1-4.1-3.1h-0.4C47.8,18.3,49.3,19.4,50.7,20.5z"
                />

                <linearGradient
                  id="SVGID_00000142150159366679577610000013943012308026823837_"
                  gradientUnits="userSpaceOnUse"
                  x1="-20.3852"
                  y1="749.85"
                  x2="50.8962"
                  y2="749.85"
                  gradientTransform="matrix(1 0 0 1 0 -740)"
                >
                  <stop offset="0" style="stop-color:#FFFFFF;stop-opacity:0" />
                  <stop offset="0.5" style="stop-color:#FFFFFF" />
                  <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
                </linearGradient>
                <path
                  style="fill:url(#SVGID_00000142150159366679577610000013943012308026823837_);"
                  d="M21.8,6.7c6.5,1.3,12.6,3.5,18.1,6.5V13
              c-5.6-3-11.7-5.3-18.2-6.5L21.8,6.7z"
                />

                <linearGradient
                  id="SVGID_00000016064222422166751810000006115439869382678957_"
                  gradientUnits="userSpaceOnUse"
                  x1="-20.384"
                  y1="745.75"
                  x2="50.8969"
                  y2="745.75"
                  gradientTransform="matrix(1 0 0 1 0 -740)"
                >
                  <stop offset="0" style="stop-color:#FFFFFF;stop-opacity:0" />
                  <stop offset="0.5" style="stop-color:#FFFFFF" />
                  <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
                </linearGradient>
                <path
                  style="fill:url(#SVGID_00000016064222422166751810000006115439869382678957_);"
                  d="M8.5,5.4c4,0,7.9,0.4,11.7,1l0.1-0.3
              c-3.8-0.7-7.7-1-11.8-1C6.3,5.1,4.1,5.2,2,5.4v0.3C4.2,5.5,6.3,5.4,8.5,5.4z"
                />
              </g>
              <g>
                <radialGradient
                  id="SVGID_00000014612755560435205980000000538294876228347553_"
                  cx="360.3222"
                  cy="1161.3466"
                  r="68.5451"
                  gradientTransform="matrix(0.7401 0 0 0.7401 -249.3551 -829.4978)"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" style="stop-color:#FFFFFF" />
                  <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
                </radialGradient>
                <path
                  style="fill:url(#SVGID_00000014612755560435205980000000538294876228347553_);"
                  d="M70.9,32.7v-9c0-1.3-1-2.2-2.2-2.2
              c-0.8,0-1.5,0.5-1.9,1v-0.9h-2.1v11.1h2.1v-9c0-0.5,0.5-1,1-1s1,0.5,1,1v9H70.9z"
                />

                <radialGradient
                  id="SVGID_00000173135137522365125670000014101007229574113983_"
                  cx="360.3102"
                  cy="1161.345"
                  r="68.5606"
                  gradientTransform="matrix(0.7401 0 0 0.7401 -249.3551 -829.4978)"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" style="stop-color:#FFFFFF" />
                  <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
                </radialGradient>
                <path
                  style="fill:url(#SVGID_00000173135137522365125670000014101007229574113983_);"
                  d="M56.2,23.6h0.2v-2h-0.2
              c-0.5,0-1.3,0-1.6,1.2h-0.1v-1.1h-2c0.8,0.7,1.6,1.5,2.5,2.3C55.3,23.6,55.8,23.6,56.2,23.6z"
                />

                <radialGradient
                  id="SVGID_00000057832901885648255930000013158392157938794393_"
                  cx="360.3131"
                  cy="1161.3468"
                  r="68.5545"
                  gradientTransform="matrix(0.7401 0 0 0.7401 -249.3551 -829.4978)"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" style="stop-color:#FFFFFF" />
                  <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
                </radialGradient>
                <path
                  style="fill:url(#SVGID_00000057832901885648255930000013158392157938794393_);"
                  d="M59.2,24c0-0.5,0.5-1,0.9-1
              c0.5,0,1,0.5,1,1v6.5c0,0.1,0,0.2,0,0.3c0.4,0.5,0.7,0.9,1.1,1.4c0.6-0.5,1-1.4,1-2.3v-5.5c0-1.6-1.4-3-3-3s-3,1.4-3,3v1.7
              c0.7,0.7,1.4,1.5,2.1,2.3L59.2,24L59.2,24z"
                />

                <radialGradient
                  id="SVGID_00000115499399206504787560000013247962261827848630_"
                  cx="360.3292"
                  cy="1161.3473"
                  r="68.5398"
                  gradientTransform="matrix(0.7401 0 0 0.7401 -249.3551 -829.4978)"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" style="stop-color:#FFFFFF" />
                  <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
                </radialGradient>
                <path
                  style="fill:url(#SVGID_00000115499399206504787560000013247962261827848630_);"
                  d="M84.4,28v-3.5c0-1.6-1.4-3-3-3
              s-3,1.4-3,3V30c0,1.6,1.4,3,3,3s3-1.4,3-3v-0.8h-2.1v1.5c0,0.5-0.5,1-1,1s-1-0.5-1-1v-2.5L84.4,28L84.4,28z M80.5,26v-2
              c0-0.5,0.5-1,1-1s1,0.5,1,1v2.5h-1.9L80.5,26L80.5,26z"
                />

                <radialGradient
                  id="SVGID_00000029755824186268642480000002160065584853901964_"
                  cx="360.3307"
                  cy="1161.3461"
                  r="68.5367"
                  gradientTransform="matrix(0.7401 0 0 0.7401 -249.3551 -829.4978)"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" style="stop-color:#FFFFFF" />
                  <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
                </radialGradient>
                <polygon
                  style="fill:url(#SVGID_00000029755824186268642480000002160065584853901964_);"
                  points="72.5,21.7 72.5,23.8 74.7,23.8
              72.2,32.7 77.3,32.7 77.3,30.7 74.9,30.7 77.5,21.7 			"
                />

                <radialGradient
                  id="SVGID_00000111164029960798263710000007913928379686086279_"
                  cx="360.3304"
                  cy="1161.3506"
                  r="68.5334"
                  gradientTransform="matrix(0.7401 0 0 0.7401 -249.3551 -829.4978)"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" style="stop-color:#FFFFFF" />
                  <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
                </radialGradient>
                <path
                  style="fill:url(#SVGID_00000111164029960798263710000007913928379686086279_);"
                  d="M47.4,17.2h-0.8c1.4,1,2.8,2,4.1,3.1
              C50.6,18.5,49.6,17.2,47.4,17.2z"
                />

                <radialGradient
                  id="SVGID_00000176729542890757707370000008603975585405245876_"
                  cx="360.3228"
                  cy="1161.3462"
                  r="68.5447"
                  gradientTransform="matrix(0.7401 0 0 0.7401 -249.3551 -829.4978)"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" style="stop-color:#FFFFFF" />
                  <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
                </radialGradient>
                <path
                  style="fill:url(#SVGID_00000176729542890757707370000008603975585405245876_);"
                  d="M20.3,6.2l0.7-3l0.8,3.4
              c6.5,1.3,12.6,3.5,18.2,6.5V1.7H2.1v3.7c2.1-0.2,4.3-0.3,6.5-0.3C12.5,5.2,16.4,5.5,20.3,6.2z"
                />
              </g>
              <g>
                <g class="st18">
                  <g>
                    <path
                      class="st19"
                      d="M39.7,2v23.7c0,2.8-0.4,5.3-1.2,7.5l-10.4-9.7l10.6-9.9l0.8-0.8l-1.1,0.4l-13.8,4.1l-3.4-14L21,2.1
                  l-0.3,1.1l-3.4,14L3.5,13.1l-1.1-0.4l0.8,0.8l10.6,9.9L3.4,33.2c-0.7-2.2-1.2-4.6-1.2-7.5V2H39.7 M24.5,29.6l13.7,4.2
                  c-2.8,7.6-9.8,12-17.3,15.5c-7.5-3.5-14.5-7.8-17.4-15.6l13.7-4.1l3.4,14.1l0.3,1.1l0.3-1.1L24.5,29.6 M40,1.7H2.1v24
                  c0,13.6,9.7,19.6,18.9,23.8c9.2-4.2,19-10.2,19-23.7V1.7L40,1.7z M17.5,17.5L21,3.3l3.4,14.3l14.1-4.2L27.8,23.5l10.7,10.1
                  l-14.1-4.3L21,43.7l-3.4-14.4L3.5,33.6l10.7-10.1L3.5,13.4L17.5,17.5L17.5,17.5z"
                    />
                  </g>
                </g>
              </g>
              <g class="st2">
                <g>
                  <path
                    class="st19"
                    d="M68.7,21.8c1.1,0,1.9,0.9,1.9,1.9v8.7H69v-8.7c0-0.6-0.5-1.3-1.3-1.3c-0.3,0-0.5,0.1-0.8,0.3v-0.1
                C67.4,22.2,68,21.8,68.7,21.8 M66.6,22v0.6v0.9l0.1-0.1c0,0.1,0,0.2,0,0.4v8.7h-1.5V22H66.6 M68.7,21.5c-0.8,0-1.5,0.5-1.9,1
                v-0.9h-2.1v11.1h2.1v-9c0-0.5,0.5-1,1-1s1,0.5,1,1v9H71v-9C70.9,22.5,69.9,21.5,68.7,21.5L68.7,21.5z"
                  />
                </g>
                <g>
                  <path
                    class="st19"
                    d="M56.1,22v1.5c-0.6,0-1.9,0.1-1.9,2v7h-1.5V22h1.5v0.8v0.3h0.3h0.1h0.2l0.1-0.2C55.1,22.1,55.6,22,56.1,22
                M56.4,21.7h-0.2c-0.5,0-1.3,0-1.6,1.2h-0.1v-1.1h-2.1v10.9h2.1v-7.3c0-1.7,1-1.7,1.6-1.7l0,0h0.3V21.7L56.4,21.7z"
                  />
                </g>
                <g>
                  <path
                    class="st19"
                    d="M60.2,21.8c1.5,0,2.7,1.3,2.7,2.7V30c0,1.5-1.3,2.7-2.7,2.7c-1.5,0-2.7-1.3-2.7-2.7v-5.5
                C57.5,23,58.6,21.8,60.2,21.8 M60.2,31.8c0.6,0,1.3-0.5,1.3-1.3V24c0-0.6-0.5-1.3-1.3-1.3c-0.6,0-1.2,0.5-1.2,1.3v6.5
                C58.9,31.3,59.5,31.8,60.2,31.8 M60.2,21.5c-1.6,0-3,1.4-3,3V30c0,1.6,1.4,3,3,3s3-1.4,3-3v-5.5C63.2,22.9,61.8,21.5,60.2,21.5
                L60.2,21.5z M60.2,31.6c-0.5,0-0.9-0.5-0.9-1V24c0-0.5,0.5-1,0.9-1c0.5,0,1,0.5,1,1v6.5C61.1,31.1,60.6,31.6,60.2,31.6
                L60.2,31.6z"
                  />
                </g>
                <g>
                  <path
                    class="st19"
                    d="M81.4,21.8c1.5,0,2.7,1.2,2.7,2.7v3.2h-3.7h-0.3V28v2.5c0,0.6,0.5,1.3,1.3,1.3s1.3-0.5,1.3-1.3v-1.2h1.5
                V30c0,1.5-1.3,2.7-2.7,2.7c-1.5,0-2.7-1.3-2.7-2.7v-5.5C78.7,23,79.8,21.8,81.4,21.8 M80.2,26.7h0.3h1.9h0.3v-0.3V24
                c0-0.6-0.5-1.3-1.3-1.3s-1.3,0.5-1.3,1.3v2v0.5L80.2,26.7 M81.4,21.5c-1.6,0-3,1.4-3,3V30c0,1.6,1.4,3,3,3s3-1.4,3-3v-0.8h-2.1
                v1.5c0,0.5-0.5,1-1,1s-1-0.5-1-1v-2.5h4v-3.5C84.4,22.9,83,21.5,81.4,21.5L81.4,21.5z M80.5,26.5V26v-2c0-0.5,0.5-1,1-1
                s1,0.5,1,1v2.5H80.5L80.5,26.5z"
                  />
                </g>
                <g>
                  <path
                    class="st19"
                    d="M77.1,22l-2.5,8.6V31H75h2v1.5h-4.5l2.5-8.6l0.1-0.4h-0.4h-2V22H77.1 M77.5,21.7h-5.1v2.1h2.4l-2.5,8.9
                h5.1v-2.1H75L77.5,21.7L77.5,21.7z"
                  />
                </g>
                <g>
                  <path
                    class="st19"
                    d="M47.4,17.5c1.9,0,3.1,1.2,3.1,3.1v2.1c0,0.7-0.6,1.7-1.8,1.8V25c1.3,0.1,1.9,1.5,1.9,2.3v2.1
                c0,1.2-0.4,3.1-3.1,3.1h-3.4v-15H47.4 M45.6,24.2h0.3h1.5h0.1c0.8-0.1,1.5-0.7,1.5-1.5v-2.5c0-0.8-0.7-1.5-1.5-1.5H46h-0.3V19v5
                L45.6,24.2 M45.6,31.4h0.3h1.6c0.8,0,1.5-0.7,1.5-1.5v-3c0-0.8-0.6-1.5-1.5-1.5h-0.1h-1.6h-0.3v0.3v5.5L45.6,31.4 M47.4,17.2
                h-3.5v15.6h3.6c2.4,0,3.4-1.5,3.4-3.4v-2.1c0-0.9-0.6-2.5-2.2-2.5c1.3-0.1,2.1-1.2,2.1-2.1v-2.1C50.7,18.6,49.6,17.2,47.4,17.2
                L47.4,17.2z M45.9,23.9v-5.1h1.5c0.7,0,1.3,0.5,1.3,1.3v2.5c0,0.7-0.5,1.3-1.2,1.3h-0.1L45.9,23.9L45.9,23.9z M45.9,31.1v-5.5
                h1.6h0.1c0.6,0,1.2,0.6,1.2,1.3v3c0,0.7-0.5,1.3-1.3,1.3L45.9,31.1L45.9,31.1z"
                  />
                </g>
              </g>
            </g>
          </g>
        </svg>
      `;
    }
    if (type === MorningstarMedalistRating.SILVER) {
      return html`
        <svg
          slot=${ifDefined(options?.slot)}
          version="1.1"
          id="Layer_1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          x="0px"
          y="0px"
          viewBox="0 0 80.7 52"
          style="enable-background:new 0 0 80.7 52;"
          xml:space="preserve"
          aria-label=${ifDefined(options?.ariaLabel)}
        >
          <style type="text/css">
            .st0 {
              fill: #ffffff;
            }
            .st1 {
              opacity: 0.15;
            }
            .st2 {
              opacity: 0.25;
            }
            .st3 {
              fill: #c8745a;
            }
            .st4 {
              fill: url(#SVGID_1_);
            }
            .st5 {
              fill: url(#SVGID_00000023973673148930367970000014443125723560212622_);
            }
            .st6 {
              fill: url(#SVGID_00000099629420448887440180000011469319202157948582_);
            }
            .st7 {
              fill: url(#SVGID_00000163752073699918877690000002084487377636260763_);
            }
            .st8 {
              fill: url(#SVGID_00000155109092616756181540000011291736820958735545_);
            }
            .st9 {
              fill: url(#SVGID_00000017510558130942805580000001314632357842859682_);
            }
            .st10 {
              fill: url(#SVGID_00000137089659558353884410000004299293996429203615_);
            }
            .st11 {
              fill: url(#SVGID_00000008857750603659654080000012295872818428166055_);
            }
            .st12 {
              fill: url(#SVGID_00000058557949132884179020000008750667707699047319_);
            }
            .st13 {
              fill: url(#SVGID_00000125595470150248525150000000971694998096594334_);
            }
            .st14 {
              fill: url(#SVGID_00000016076563884647848640000008377736428597903792_);
            }
            .st15 {
              fill: url(#SVGID_00000036229363431020857220000005942631318945007511_);
            }
            .st16 {
              fill: url(#SVGID_00000123428027137413244600000001984400046498216847_);
            }
            .st17 {
              fill: url(#SVGID_00000024694281915442722460000015207053163524315042_);
            }
            .st18 {
              opacity: 0.45;
            }
            .st19 {
              fill: #774331;
            }
            .st20 {
              opacity: 0.15;
              enable-background: new;
            }
            .st21 {
              opacity: 0.25;
              enable-background: new;
            }
            .st22 {
              fill: #faa41d;
            }
            .st23 {
              fill: url(#SVGID_00000082337497492015710360000006867401898903167133_);
            }
            .st24 {
              fill: url(#SVGID_00000043441982815533572160000011707031018642818711_);
            }
            .st25 {
              fill: url(#SVGID_00000064338908420157933160000016218647182108847531_);
            }
            .st26 {
              fill: url(#SVGID_00000109723366798494143590000001556301410437782188_);
            }
            .st27 {
              fill: url(#SVGID_00000111905640248618345570000004232440554409895067_);
            }
            .st28 {
              fill: url(#SVGID_00000153688586444932859800000005374436929848570784_);
            }
            .st29 {
              fill: url(#SVGID_00000042711658481452027130000001296145952228632763_);
            }
            .st30 {
              fill: url(#SVGID_00000067207895364835144580000008635615482477025202_);
            }
            .st31 {
              fill: url(#SVGID_00000034049509596554098470000009187440408788882867_);
            }
            .st32 {
              fill: url(#SVGID_00000166651250245575772520000006945254654580699303_);
            }
            .st33 {
              fill: url(#SVGID_00000133503766141099086130000015048189389694987170_);
            }
            .st34 {
              fill: url(#SVGID_00000168822041642025742420000002726576775266217094_);
            }
            .st35 {
              opacity: 0.45;
              fill: #915f02;
              enable-background: new;
            }
            .st36 {
              opacity: 0.25;
              fill: #915f02;
              enable-background: new;
            }
            .st37 {
              fill: #8b9b97;
            }
            .st38 {
              fill: url(#SVGID_00000088844771609170167340000004144008610454886535_);
            }
            .st39 {
              fill: url(#SVGID_00000134963370611942692660000013534073239377913238_);
            }
            .st40 {
              fill: url(#SVGID_00000159470667068769702450000013528989046792540595_);
            }
            .st41 {
              fill: url(#SVGID_00000034081269806884437120000000926844021561905284_);
            }
            .st42 {
              fill: url(#SVGID_00000178921585847793218590000006012658192842632121_);
            }
            .st43 {
              fill: url(#SVGID_00000149366035338741541600000000714998235811493761_);
            }
            .st44 {
              fill: url(#SVGID_00000033350838901398260970000004486572440616724363_);
            }
            .st45 {
              fill: url(#SVGID_00000142860629679387013330000008098710379291166139_);
            }
            .st46 {
              fill: url(#SVGID_00000117663616714245674220000017343340960808871079_);
            }
            .st47 {
              fill: url(#SVGID_00000103227271040255970150000006664721901724006020_);
            }
            .st48 {
              fill: url(#SVGID_00000062161148839651881550000012093636937157874324_);
            }
            .st49 {
              fill: url(#SVGID_00000175291727970194918650000011255562290698211240_);
            }
            .st50 {
              fill: url(#SVGID_00000119110080455383221680000011914294898176011163_);
            }
            .st51 {
              fill: url(#SVGID_00000000922816604775750050000007987289494050728869_);
            }
            .st52 {
              fill: url(#SVGID_00000150072789403172942530000007221333192572535728_);
            }
            .st53 {
              opacity: 0.45;
              fill: #596563;
              enable-background: new;
            }
            .st54 {
              opacity: 0.25;
              fill: #596563;
              enable-background: new;
            }
          </style>
          <g>
            <path
              class="st0"
              d="M2.2,2v24c0,13.5,9.7,19.5,18.9,23.8c9.3-4.2,19-10.2,19-23.7V2H2.2z"
            />
            <g class="st1">
              <g class="st1">
                <g class="st1">
                  <g class="st1">
                    <g class="st1">
                      <path
                        class="st20"
                        d="M44,27.3v2.4c0,1.9,1.5,3.4,3.4,3.4c1.9,0,3.4-1.5,3.4-3.4v-0.8c0-1.6-1.3-3.1-2.5-4.5
							          c-1-1.2-2.1-2.4-2.1-3.3v-1c0-0.7,0.5-1.3,1.3-1.3c0.7,0,1.3,0.5,1.3,1.3v2.2h2v-2c0-1.8-1.5-3.3-3.3-3.3
							          c-1.7,0-3.2,1.4-3.3,3v1.1c0,1.7,1.4,3.3,2.6,4.6c1,1.1,2,2.3,2,3.1v1.1c0,0.7-0.6,1.4-1.4,1.4c-0.7,0-1.4-0.6-1.4-1.4v-2.6
							          H44z"
                      />
                    </g>
                    <path class="st20" d="M54.4,32.7h-2V22h2V32.7z" />
                    <path
                      class="st20"
                      d="M53.4,16.9c-0.7,0-1.3,0.5-1.3,1.3c0,0.7,0.5,1.3,1.3,1.3s1.3-0.5,1.3-1.3C54.6,17.5,54.1,16.9,53.4,16.9
						          z"
                    />
                    <path class="st20" d="M58.5,32.7h-2V17.3h2V32.7z" />
                    <path
                      class="st20"
                      d="M64.3,32.7L66.5,22h-2.1L63,30.7l-1.3-8.8L59.5,22l2.3,10.7H64.3z"
                    />
                    <path
                      class="st20"
                      d="M70.2,33c-1.6,0-3-1.4-3-3v-5.4c0-1.6,1.4-3,3-3c1.6,0,3,1.4,3,3v3.5h-3.9v2.5c0,0.5,0.5,0.9,0.9,0.9
						          c0.5,0,0.9-0.5,0.9-0.9v-1.4h2v0.8C73.2,31.7,71.8,33,70.2,33z M69.3,26.5h1.9v-2.5c0-0.5-0.5-0.9-0.9-0.9
						          c-0.5,0-0.9,0.5-0.9,0.9v2L69.3,26.5L69.3,26.5z"
                    />
                    <path
                      class="st20"
                      d="M76.6,25.5c0-1.7,1.1-1.7,1.6-1.7h0.2v-2h-0.2c-0.5,0-1.3,0-1.6,1.2h-0.1v-1.1h-2v10.8h2L76.6,25.5
						          L76.6,25.5z"
                    />
                  </g>
                </g>
              </g>
            </g>
            <g class="st1">
              <g class="st1">
                <g class="st1">
                  <g class="st1">
                    <g class="st1">
                      <path
                        class="st20"
                        d="M44,27.3v2.4c0,1.9,1.5,3.4,3.4,3.4c1.9,0,3.4-1.5,3.4-3.4v-0.8c0-1.6-1.3-3.1-2.5-4.5
							          c-1-1.2-2.8-2.4-2.8-3.3v-1.6c0-0.7,1.3-1.9,1.9-1.9c0.7,0,1.3,1.3,1.3,1.9v2.8h2v-2c0-1.8-1.5-3.3-3.3-3.3
							          c-1.7,0-3.2,1.4-3.3,3v1.1c0,1.7,2,3.3,3.4,4.6c1,1.1,2,2.3,2,3.1v1.1c0,0.7-1.3,2.7-2,2.7c-1.2,0-2.7-1.9-2.7-2.7v-2.6H44z"
                      />
                    </g>
                    <path class="st20" d="M54.4,32.8h-2V22h2V32.8z" />
                    <path
                      class="st20"
                      d="M53.4,16.9c-0.7,0-1.3,0.5-1.3,1.3c0,0.7,0.5,1.3,1.3,1.3s1.3-0.5,1.3-1.3C54.6,17.5,54.1,16.9,53.4,16.9
						          z"
                    />
                    <path class="st20" d="M58.5,32.8h-2V17.3h2V32.8z" />
                    <path
                      class="st20"
                      d="M64.3,32.7L66.5,22h-2.1L63,30.7l-0.7-0.5L61,22.6L59.5,22l2.3,10.7H64.3z"
                    />
                    <path
                      class="st20"
                      d="M70.2,33.1c-1.6,0-3-1.4-3-3v-5.4c0-1.6,1.4-3,3-3c1.6,0,3,1.4,3,3v2.8h-4.6v3.2c0,0.5,0.5,1.6,0.9,1.6
						          c0.5,0,1.6,0.3,1.6-0.3v-2.7h2v0.8C73.2,31.7,71.8,33.1,70.2,33.1z M67.9,26.6h3.3v-3.8c0-0.5-0.5-0.8-0.9-0.8
						          c-1.5,0-2.3,1.6-2.3,2.2v2L67.9,26.6L67.9,26.6z"
                    />
                    <path
                      class="st20"
                      d="M76.6,25.6c0-1.7,1.1-1.7,1.6-1.7h0.2v-2h-0.2c-0.5,0-1.3,0-1.6,1.2h-0.1V22h-2v10.8h2L76.6,25.6
						          L76.6,25.6z"
                    />
                  </g>
                </g>
              </g>
            </g>
            <g class="st2">
              <g class="st2">
                <g class="st2">
                  <path
                    class="st21"
                    d="M2.2,2v24c0,13.5,9.7,19.5,18.9,23.8c9.3-4.2,19-10.2,19-23.7V2H2.2z M24.6,29.5l-3.4,14.4l-3.4-14.4
					          L3.7,33.8l10.7-10.1L3.7,13.6l14.1,4.2l3.5-14.3l3.4,14.3l14.1-4.2L27.9,23.7l10.7,10.1L24.6,29.5z"
                  />
                </g>
              </g>
            </g>
            <path
              class="st37"
              d="M44,27.3v2.4c0,1.9,1.5,3.4,3.4,3.4c1.9,0,3.4-1.5,3.4-3.4v-0.8c0-1.6-1.3-3.1-2.5-4.5
		          c-1-1.2-2.1-2.4-2.1-3.3v-1c0-0.7,0.5-1.3,1.3-1.3c0.7,0,1.3,0.5,1.3,1.3v2.2h2v-2c0-1.8-1.5-3.3-3.3-3.3c-1.7,0-3.2,1.4-3.3,3v1.1
		          c0,1.7,1.4,3.3,2.6,4.6c1,1.1,2,2.3,2,3.1v1.1c0,0.7-0.6,1.4-1.4,1.4c-0.7,0-1.4-0.6-1.4-1.4v-2.6H44z"
            />
            <path class="st37" d="M54.4,32.8h-2V22h2V32.8z" />
            <path
              class="st37"
              d="M53.4,16.9c-0.7,0-1.3,0.5-1.3,1.3c0,0.7,0.5,1.3,1.3,1.3s1.3-0.5,1.3-1.3C54.6,17.5,54.1,16.9,53.4,16.9z"
            />
            <path class="st37" d="M58.5,32.8h-2V17.3h2V32.8z" />
            <path
              class="st37"
              d="M64.3,32.7L66.5,22h-2.1L63,30.7L61.7,22h-2.2l2.3,10.7H64.3z"
            />
            <path
              class="st37"
              d="M70.2,33.1c-1.6,0-3-1.4-3-3v-5.4c0-1.6,1.4-3,3-3c1.6,0,3,1.4,3,3v3.5h-3.9v2.5c0,0.5,0.5,0.9,0.9,0.9
		          c0.5,0,0.9-0.5,0.9-0.9v-1.4h2v0.8C73.2,31.7,71.8,33.1,70.2,33.1z M69.3,26.6h1.9v-2.5c0-0.5-0.5-0.9-0.9-0.9
		          c-0.5,0-0.9,0.5-0.9,0.9v2L69.3,26.6L69.3,26.6z"
            />
            <path
              class="st37"
              d="M76.6,25.6c0-1.7,1.1-1.7,1.6-1.7h0.2v-2h-0.2c-0.5,0-1.3,0-1.6,1.2h-0.1V22h-2v10.8h2L76.6,25.6L76.6,25.6z"
            />
            <path class="st37" d="M71.9,35.2h0.9v2.4H73v-2.4h0.9V35h-2V35.2z" />
            <path
              class="st37"
              d="M75.4,37.2L74.5,35h-0.4v2.5h0.2v-2.3l0.9,2.3h0.3l0.8-2.3v2.3h0.3V35h-0.4L75.4,37.2z"
            />
            <path
              class="st37"
              d="M2.2,2v24c0,13.5,9.7,19.5,18.9,23.8c9.3-4.2,19-10.2,19-23.7V2H2.2z M24.6,29.5l-3.4,14.4l-3.4-14.4
		          L3.7,33.8l10.7-10.1L3.7,13.6l14.1,4.2l3.5-14.3l3.4,14.3l14.1-4.2L27.9,23.7l10.7,10.1L24.6,29.5z"
            />

            <linearGradient
              id="SVGID_1_"
              gradientUnits="userSpaceOnUse"
              x1="21.1465"
              y1="-247.211"
              x2="21.1465"
              y2="-214.3724"
              gradientTransform="matrix(1 0 0 -1 0 -185.8113)"
            >
              <stop offset="0" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </linearGradient>
            <path
              class="st4"
              d="M2.2,2v24c0,13.5,9.7,19.5,18.9,23.8c9.3-4.2,19-10.2,19-23.7V2H2.2z M24.6,29.5l-3.4,14.4l-3.4-14.4L3.7,33.8
		          l10.7-10.1L3.7,13.6l14.1,4.2l3.5-14.3l3.4,14.3l14.1-4.2L27.9,23.7l10.7,10.1L24.6,29.5z"
            />

            <radialGradient
              id="SVGID_00000163072993474294049780000018215887645861949115_"
              cx="-252.7747"
              cy="298.9169"
              r="0.469"
              gradientTransform="matrix(107.754 0 0 -107.754 27254.9805 32239.709)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </radialGradient>
            <path
              style="fill:url(#SVGID_00000163072993474294049780000018215887645861949115_);"
              d="M50.7,20.3c0-1.8-1.5-3.3-3.3-3.3
		          c-0.4,0-0.7,0.1-1.1,0.2c1.5,1,3,2.1,4.4,3.3V20.3z"
            />

            <radialGradient
              id="SVGID_00000098178572975837794230000011472335432284917672_"
              cx="-252.775"
              cy="298.9175"
              r="0.469"
              gradientTransform="matrix(107.77 0 0 -107.77 27259.0469 32244.5625)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </radialGradient>
            <path
              style="fill:url(#SVGID_00000098178572975837794230000011472335432284917672_);"
              d="M54.4,22h-1.8c0.6,0.5,1.3,1.1,1.8,1.6V22
		          z"
            />

            <radialGradient
              id="SVGID_00000182496232141797551600000010191887820320157107_"
              cx="-252.7734"
              cy="298.9139"
              r="0.469"
              gradientTransform="matrix(107.676 0 0 -107.676 27235.1504 32216.0684)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </radialGradient>
            <path
              style="fill:url(#SVGID_00000182496232141797551600000010191887820320157107_);"
              d="M52.1,18.2c0,0.7,0.5,1.3,1.3,1.3
		          s1.3-0.5,1.3-1.3c0-0.7-0.5-1.3-1.3-1.3C52.7,16.9,52.1,17.5,52.1,18.2z"
            />

            <radialGradient
              id="SVGID_00000161603783081208345540000010609364083494625465_"
              cx="-252.7749"
              cy="298.9173"
              r="0.469"
              gradientTransform="matrix(107.764 0 0 -107.764 27257.5195 32242.7402)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </radialGradient>
            <path
              style="fill:url(#SVGID_00000161603783081208345540000010609364083494625465_);"
              d="M58.5,17.3h-2v8.5c0.7,0.7,1.4,1.5,2,2.2
		          V17.3z"
            />

            <radialGradient
              id="SVGID_00000180360163846886532670000015225294974429638035_"
              cx="-252.7746"
              cy="298.9166"
              r="0.469"
              gradientTransform="matrix(107.746 0 0 -107.746 27252.9453 32237.2852)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </radialGradient>
            <path
              style="fill:url(#SVGID_00000180360163846886532670000015225294974429638035_);"
              d="M64.3,32.7l2.3-10.8h-2.2L63,30.7
		          l-1.4-8.8h-2.2l2,9.6c0.3,0.4,0.6,0.8,0.9,1.2H64.3z"
            />

            <radialGradient
              id="SVGID_00000023270174510145209750000005592086523668882586_"
              cx="-252.7744"
              cy="298.916"
              r="0.469"
              gradientTransform="matrix(107.731 0 0 -107.731 27249.1348 32232.7383)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </radialGradient>
            <path
              style="fill:url(#SVGID_00000023270174510145209750000005592086523668882586_);"
              d="M73.2,28.1v-3.5c0-1.6-1.4-3-3-3
		          c-1.6,0-3,1.4-3,3V30c0,1.6,1.4,3,3,3c1.6,0,3-1.4,3-3v-0.8h-2v1.4c0,0.5-0.5,0.9-0.9,0.9c-0.5,0-0.9-0.5-0.9-0.9V28L73.2,28.1
		          L73.2,28.1z M69.3,26.1v-1.9c0-0.5,0.5-0.9,0.9-0.9c0.5,0,0.9,0.5,0.9,0.9v2.5h-1.9L69.3,26.1L69.3,26.1z"
            />

            <radialGradient
              id="SVGID_00000003825976246197283370000011712996294809842084_"
              cx="-252.7738"
              cy="298.9147"
              r="0.469"
              gradientTransform="matrix(107.697 0 0 -107.697 27240.5039 32222.4336)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </radialGradient>
            <path
              style="fill:url(#SVGID_00000003825976246197283370000011712996294809842084_);"
              d="M78.4,21.9h-0.2c-0.5,0-1.3,0-1.6,1.2
		          h-0.1V22h-2v10.8h2v-7.2c0-1.7,1.1-1.7,1.6-1.7h0.2L78.4,21.9L78.4,21.9z"
            />

            <radialGradient
              id="SVGID_00000047034494340882054280000014859351212734846907_"
              cx="-252.7746"
              cy="298.9165"
              r="0.469"
              gradientTransform="matrix(107.744 0 0 -107.744 27252.4395 32236.6797)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </radialGradient>
            <path
              style="fill:url(#SVGID_00000047034494340882054280000014859351212734846907_);"
              d="M20.5,6.5l0.7-3L22,6.7
		          c6.5,1.3,12.5,3.5,18.2,6.5V2H2.3v3.7c2.1-0.2,4.3-0.3,6.5-0.3C12.6,5.5,16.6,5.8,20.5,6.5z"
            />

            <linearGradient
              id="SVGID_00000132767239045188304260000004478360085413228955_"
              gradientUnits="userSpaceOnUse"
              x1="-20.1539"
              y1="-204.7632"
              x2="50.895"
              y2="-204.7632"
              gradientTransform="matrix(1 0 0 -1 0 -185.8113)"
            >
              <stop offset="0" style="stop-color:#FFFFFF;stop-opacity:0" />
              <stop offset="0.5" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </linearGradient>
            <path
              style="fill:url(#SVGID_00000132767239045188304260000004478360085413228955_);"
              d="M50.7,20.7v-0.3c-1.5-1.2-2.9-2.3-4.4-3.3
		          c-0.1,0-0.2,0.1-0.3,0.1C47.6,18.4,49.2,19.5,50.7,20.7z"
            />

            <linearGradient
              id="SVGID_00000147942291138504944090000012887047336995410356_"
              gradientUnits="userSpaceOnUse"
              x1="-20.1276"
              y1="-208.7616"
              x2="50.895"
              y2="-208.7616"
              gradientTransform="matrix(1 0 0 -1 0 -185.8113)"
            >
              <stop offset="0" style="stop-color:#FFFFFF;stop-opacity:0" />
              <stop offset="0.5" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </linearGradient>
            <path
              style="fill:url(#SVGID_00000147942291138504944090000012887047336995410356_);"
              d="M52.4,22.1c0.7,0.6,1.4,1.2,2,1.8v-0.3
		          c-0.6-0.5-1.2-1.1-1.8-1.6L52.4,22.1L52.4,22.1z"
            />

            <linearGradient
              id="SVGID_00000103244857213839976600000005038254083325761939_"
              gradientUnits="userSpaceOnUse"
              x1="-20.1621"
              y1="-212.851"
              x2="50.8926"
              y2="-212.851"
              gradientTransform="matrix(1 0 0 -1 0 -185.8113)"
            >
              <stop offset="0" style="stop-color:#FFFFFF;stop-opacity:0" />
              <stop offset="0.5" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </linearGradient>
            <path
              style="fill:url(#SVGID_00000103244857213839976600000005038254083325761939_);"
              d="M58.5,28.3V28c-0.6-0.7-1.4-1.5-2-2.2v0.3
		          C57.3,26.8,57.9,27.5,58.5,28.3z"
            />

            <linearGradient
              id="SVGID_00000018218136309706024110000014683883695304139949_"
              gradientUnits="userSpaceOnUse"
              x1="-20.15"
              y1="-217.9399"
              x2="50.894"
              y2="-217.9399"
              gradientTransform="matrix(1 0 0 -1 0 -185.8113)"
            >
              <stop offset="0" style="stop-color:#FFFFFF;stop-opacity:0" />
              <stop offset="0.5" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </linearGradient>
            <path
              style="fill:url(#SVGID_00000018218136309706024110000014683883695304139949_);"
              d="M62.2,32.7h0.3c-0.3-0.4-0.6-0.8-0.9-1.2
		          l0.1,0.5C61.8,32.3,62,32.5,62.2,32.7z"
            />

            <linearGradient
              id="SVGID_00000129896842545169304290000006404135221241887635_"
              gradientUnits="userSpaceOnUse"
              x1="-20.1451"
              y1="-191.9498"
              x2="50.894"
              y2="-191.9498"
              gradientTransform="matrix(1 0 0 -1 0 -185.8113)"
            >
              <stop offset="0" style="stop-color:#FFFFFF;stop-opacity:0" />
              <stop offset="0.5" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </linearGradient>
            <path
              style="fill:url(#SVGID_00000129896842545169304290000006404135221241887635_);"
              d="M8.7,5.7c4,0,7.9,0.4,11.7,1l0.1-0.2
		          c-3.8-0.7-7.7-1-11.8-1c-2.2,0-4.4,0.1-6.5,0.3V6C4.3,5.8,6.5,5.7,8.7,5.7z"
            />

            <linearGradient
              id="SVGID_00000124873667986981015120000013342787792748586894_"
              gradientUnits="userSpaceOnUse"
              x1="-20.147"
              y1="-195.9483"
              x2="50.8964"
              y2="-195.9483"
              gradientTransform="matrix(1 0 0 -1 0 -185.8113)"
            >
              <stop offset="0" style="stop-color:#FFFFFF;stop-opacity:0" />
              <stop offset="0.5" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </linearGradient>
            <path
              style="fill:url(#SVGID_00000124873667986981015120000013342787792748586894_);"
              d="M22,7c6.5,1.3,12.5,3.5,18.1,6.5v-0.3
		          c-5.6-3-11.7-5.3-18.2-6.5L22,7z"
            />
            <g class="st18">
              <g class="st18">
                <path
                  class="st53"
                  d="M39.8,2.3V26c0,2.8-0.4,5.3-1.2,7.5l-10.3-9.7l10.5-9.9l0.8-0.8l-1.1,0.4l-13.8,4.1l-3.4-14l-0.4-1.1
				          l-0.3,1.1l-3.3,14L3.7,13.4L2.7,13l0.8,0.8l10.5,9.9L3.7,33.4c-0.7-2.2-1.2-4.6-1.2-7.5V2.3H39.8z M24.7,29.9l13.7,4.2
				          c-2.8,7.7-9.9,12.1-17.4,15.5c-7.5-3.5-14.4-7.8-17.4-15.5l13.8-4.2L20.9,44l0.3,1.1l0.3-1.1L24.7,29.9z M40.1,2H2.2v24
				          c0,13.5,9.7,19.5,18.9,23.8c9.3-4.2,19-10.2,19-23.7V2z M17.7,17.8l3.5-14.3l3.4,14.3l14.1-4.2L27.9,23.7l10.7,10.1l-14.1-4.3
				          l-3.4,14.4l-3.4-14.4L3.7,33.8l10.7-10.1L3.7,13.6L17.7,17.8z"
                />
              </g>
            </g>
            <g class="st2">
              <g class="st2">
                <g class="st2">
                  <g class="st2">
                    <path
                      class="st54"
                      d="M47.4,17.2c1.6,0,3,1.4,3,3v1.7h-1.5V20c0-0.8-0.7-1.5-1.5-1.5c-0.8,0-1.5,0.7-1.5,1.5v1
						          c0,1.1,1.1,2.3,2.2,3.5c1.2,1.4,2.5,2.7,2.5,4.3v0.9c0,1.7-1.4,3.1-3.1,3.1c-1.7,0-3.1-1.4-3.1-3.1v-2.2h1.5v2.4
						          c0,0.9,0.7,1.6,1.6,1.6c0.9,0,1.6-0.7,1.6-1.6v-1.1c0-1-1.1-2.2-2.1-3.3c-1.3-1.4-2.5-2.8-2.5-4.5v-1
						          C44.5,18.5,45.8,17.2,47.4,17.2z M47.4,16.9c-1.7,0-3.2,1.4-3.3,3V21c0,1.7,1.4,3.3,2.6,4.6c1,1.1,2,2.3,2,3.1v1.1
						          c0,0.7-0.6,1.4-1.4,1.4c-0.7,0-1.4-0.6-1.4-1.4v-2.6h-2v2.5c0,1.9,1.5,3.4,3.4,3.4c1.9,0,3.4-1.5,3.4-3.4v-0.8
						          c0-1.6-1.3-3.1-2.5-4.5c-1-1.2-2.1-2.4-2.1-3.3v-1c0-0.7,0.5-1.3,1.3-1.3c0.7,0,1.3,0.5,1.3,1.3v2.2h2v-2
						          C50.7,18.5,49.2,16.9,47.4,16.9z"
                    />
                  </g>
                </g>
                <g class="st2">
                  <path
                    class="st54"
                    d="M54.1,22.3v10.3h-1.5V22.3H54.1z M54.4,22h-2v10.8h2V22z"
                  />
                </g>
                <g class="st2">
                  <path
                    class="st54"
                    d="M53.4,17.2c0.5,0,1,0.5,1,1c0,0.5-0.5,1-1,1c-0.5,0-1-0.5-1-1C52.4,17.6,52.8,17.2,53.4,17.2z M53.4,16.9
					          c-0.7,0-1.3,0.5-1.3,1.3c0,0.7,0.5,1.3,1.3,1.3s1.3-0.5,1.3-1.3C54.6,17.5,54.1,16.9,53.4,16.9z"
                  />
                </g>
                <g class="st2">
                  <path
                    class="st54"
                    d="M58.3,17.5v14.9h-1.5V17.5H58.3z M58.5,17.3h-2v15.4h2V17.3z"
                  />
                </g>
                <g class="st2">
                  <path
                    class="st54"
                    d="M61.4,22.2l1.3,8.5l0.3,1.7h-1l-2.2-10.3H61.4z M66.3,22.3l-2.2,10.3h-1l0.3-1.7l1.4-8.5H66.3z M61.7,21.9
					          h-2.2l2.3,10.8h2.5l2.3-10.8h-2.2L63,30.7L61.7,21.9z"
                  />
                </g>
                <g class="st2">
                  <path
                    class="st54"
                    d="M70.2,21.9c1.5,0,2.7,1.2,2.7,2.7v3.2h-3.6H69v0.3v2.5c0,0.6,0.5,1.2,1.2,1.2c0.6,0,1.2-0.5,1.2-1.2v-1.1
					          h1.5v0.5c0,1.5-1.2,2.7-2.7,2.7c-1.5,0-2.7-1.2-2.7-2.7v-5.4C67.4,23.2,68.7,21.9,70.2,21.9z M69,26.8h0.3h1.9h0.3v-0.3v-2.5
					          c0-0.6-0.5-1.2-1.2-1.2s-1.2,0.5-1.2,1.2v2v0.5L69,26.8L69,26.8z M70.2,21.6c-1.6,0-3,1.4-3,3V30c0,1.6,1.4,3,3,3
					          c1.6,0,3-1.4,3-3v-0.8h-2v1.4c0,0.5-0.5,0.9-0.9,0.9c-0.5,0-0.9-0.5-0.9-0.9V28h3.9v-3.5C73.2,23,71.8,21.6,70.2,21.6z
					           M69.3,26.5V26v-2c0-0.5,0.5-0.9,0.9-0.9c0.5,0,0.9,0.5,0.9,0.9v2.5L69.3,26.5L69.3,26.5z"
                  />
                </g>
                <g class="st2">
                  <path
                    class="st54"
                    d="M78.2,22.2v1.5c-0.6,0-1.9,0.1-1.9,2v6.9h-1.5V22.2h1.5V23v0.3h0.3h0.1h0.2l0.1-0.2
					          C77.2,22.3,77.6,22.2,78.2,22.2z M78.4,21.9h-0.2c-0.5,0-1.3,0-1.6,1.2h-0.1V22h-2v10.8h2v-7.2c0-1.7,1-1.7,1.6-1.7h0.2
					          L78.4,21.9L78.4,21.9z"
                  />
                </g>
              </g>
            </g>
          </g>
        </svg>
      `;
    }
    if (type === MorningstarMedalistRating.GOLD) {
      return html`
        <svg
          slot=${ifDefined(options?.slot)}
          version="1.1"
          id="Layer_1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          x="0px"
          y="0px"
          viewBox="0 0 73.2 52"
          style="enable-background:new 0 0 73.2 52;"
          xml:space="preserve"
          aria-label=${ifDefined(options?.ariaLabel)}
        >
          <style type="text/css">
            .st0 {
              fill: #ffffff;
            }
            .st1 {
              opacity: 0.15;
            }
            .st2 {
              opacity: 0.25;
            }
            .st3 {
              fill: #c8745a;
            }
            .st4 {
              fill: url(#SVGID_1_);
            }
            .st5 {
              fill: url(#SVGID_00000030458958502108645990000017389801602236773054_);
            }
            .st6 {
              fill: url(#SVGID_00000109724532718955285950000013544449555948043431_);
            }
            .st7 {
              fill: url(#SVGID_00000005235272972339668800000000175564432172269758_);
            }
            .st8 {
              fill: url(#SVGID_00000045580242034251941640000003527570927430351778_);
            }
            .st9 {
              fill: url(#SVGID_00000070082608182136151240000000814973559528468408_);
            }
            .st10 {
              fill: url(#SVGID_00000105426565924581260280000007981665835775386764_);
            }
            .st11 {
              fill: url(#SVGID_00000097475822991859067810000017833130683294993034_);
            }
            .st12 {
              fill: url(#SVGID_00000047038218416122611970000014627585348838780307_);
            }
            .st13 {
              fill: url(#SVGID_00000030473435238673544670000001228509538317478069_);
            }
            .st14 {
              fill: url(#SVGID_00000011749670358758556220000008660726539745328259_);
            }
            .st15 {
              fill: url(#SVGID_00000176736162067100096180000008373631837042825385_);
            }
            .st16 {
              fill: url(#SVGID_00000176723089274100801130000006050904502658200966_);
            }
            .st17 {
              fill: url(#SVGID_00000109716109035600416080000003559434131202118791_);
            }
            .st18 {
              opacity: 0.45;
            }
            .st19 {
              fill: #774331;
            }
            .st20 {
              opacity: 0.15;
              enable-background: new;
            }
            .st21 {
              opacity: 0.25;
              enable-background: new;
            }
            .st22 {
              fill: #faa41d;
            }
            .st23 {
              fill: url(#SVGID_00000036932264786829114900000004542306566371849630_);
            }
            .st24 {
              fill: url(#SVGID_00000062182259249923591520000004693485324464105090_);
            }
            .st25 {
              fill: url(#SVGID_00000063618210792507061940000010724555596482388648_);
            }
            .st26 {
              fill: url(#SVGID_00000043456064257514847090000001737521613683212931_);
            }
            .st27 {
              fill: url(#SVGID_00000065763158689586193650000001339745383673737882_);
            }
            .st28 {
              fill: url(#SVGID_00000139284781893884106930000007463393515346121870_);
            }
            .st29 {
              fill: url(#SVGID_00000168797629288760803270000008451786713197452928_);
            }
            .st30 {
              fill: url(#SVGID_00000000902877802989488140000007438969945338158512_);
            }
            .st31 {
              fill: url(#SVGID_00000034088402396029194900000002046975556400614582_);
            }
            .st32 {
              fill: url(#SVGID_00000097481923997469417980000009741591732082278552_);
            }
            .st33 {
              fill: url(#SVGID_00000142877320505341157170000017803098504835950236_);
            }
            .st34 {
              fill: url(#SVGID_00000065761062968991179180000001296254983782091139_);
            }
            .st35 {
              opacity: 0.45;
              fill: #915f02;
              enable-background: new;
            }
            .st36 {
              opacity: 0.25;
              fill: #915f02;
              enable-background: new;
            }
            .st37 {
              fill: #8b9b97;
            }
            .st38 {
              fill: url(#SVGID_00000094587390191909872370000009751459249765854623_);
            }
            .st39 {
              fill: url(#SVGID_00000161601659328492208760000004785664169535590821_);
            }
            .st40 {
              fill: url(#SVGID_00000001623511203568585420000016790682090065207467_);
            }
            .st41 {
              fill: url(#SVGID_00000075160073483902824810000004338770015727348367_);
            }
            .st42 {
              fill: url(#SVGID_00000000180375015098624550000002392101637865717164_);
            }
            .st43 {
              fill: url(#SVGID_00000005235822153838181040000017048633412333303701_);
            }
            .st44 {
              fill: url(#SVGID_00000119085825319155010440000002971095174017307054_);
            }
            .st45 {
              fill: url(#SVGID_00000114787285996292676890000000304440226345931444_);
            }
            .st46 {
              fill: url(#SVGID_00000155830404663695030840000006794492063295370685_);
            }
            .st47 {
              fill: url(#SVGID_00000117678231309616123450000013231775097366176152_);
            }
            .st48 {
              fill: url(#SVGID_00000153666121196114014460000006643419874041707905_);
            }
            .st49 {
              fill: url(#SVGID_00000076586348100467997260000001204684320531979147_);
            }
            .st50 {
              fill: url(#SVGID_00000080199058798714688440000011706294466639555500_);
            }
            .st51 {
              fill: url(#SVGID_00000135661471918716440850000010806110717356121739_);
            }
            .st52 {
              fill: url(#SVGID_00000140705138827107274290000012282162383905283993_);
            }
            .st53 {
              opacity: 0.45;
              fill: #596563;
              enable-background: new;
            }
            .st54 {
              opacity: 0.25;
              fill: #596563;
              enable-background: new;
            }
          </style>
          <g>
            <path
              class="st0"
              d="M2.2,2v24c0,13.5,9.7,19.5,18.9,23.8C30.2,45.5,40,39.5,40,26V2H2.2z"
            />
            <g class="st1">
              <g class="st1">
                <path
                  class="st20"
                  d="M47.5,24.7v1.8h1.5v3.5c0,0.8-0.6,1.5-1.5,1.5S46,30.9,46,30.1v-9.9c0-0.8,0.6-1.5,1.5-1.5s1.5,0.6,1.5,1.5
              v2.7h2v-2.4c0-1.9-1.5-3.5-3.4-3.5c-1.9,0-3.4,1.5-3.4,3.5v9.2c0,1.9,1.5,3.5,3.4,3.5c1.9,0,3.4-1.5,3.4-3.5v-5H47.5z"
                />
              </g>
            </g>
            <g class="st1">
              <g class="st1">
                <path
                  class="st20"
                  d="M47.5,24.7v1.2h1.5v4.8c0,0.8-1.6,1.8-2.7,1.5c-0.7-0.3-1.5-1.3-1.5-2.1v-9.9c0-0.8,0.5-2.1,1.3-2.5
              c0.7-0.4,3-0.4,3,2.5v2.7h2v-2.4c0-1.9-1.5-3.5-3.4-3.5c-1.9,0-3.4,1.5-3.4,3.5v9.2c0,1.9,1.5,3.5,3.4,3.5c1.9,0,3.4-1.5,3.4-3.5
              v-5H47.5z"
                />
              </g>
            </g>
            <g class="st1">
              <g class="st1">
                <path
                  class="st20"
                  d="M68.9,17.4v5.5h-0.1c-0.4-0.6-0.9-1.2-1.7-1.2c-1.2,0-2.1,0.9-2.1,2.1v7.3c0,1.2,0.9,2.1,2.1,2.1
              c0.8,0,1.4-0.5,1.7-1.1h0.1l0.1,0.8h1.9V17.4H68.9z M68.9,30.7c0,0.5-0.5,0.9-0.9,0.9c-0.5,0-0.9-0.5-0.9-0.9v-6.5
              c0-0.5,0.5-0.9,0.9-0.9c0.5,0,0.9,0.5,0.9,0.9V30.7z"
                />
              </g>
            </g>
            <g class="st1">
              <g class="st1">
                <path
                  class="st20"
                  d="M68.9,17.4v6h-0.1c-0.4-0.6-0.9-1.5-1.7-1.5c-1.2,0-2.1,0.9-2.1,2.1v7.3c0,1.2,0.9,2.1,2.1,2.1
              c0.8,0,1.4-0.5,1.7-1.1h0.1L69,33h1.9V17.4H68.9z M68.9,31.4c0,0.5-0.9,0.9-2.3,0.9c-0.5,0-0.9-1.1-0.9-1.6v-6.5
              c0-0.5,0.5-1.4,0.9-1.6c0.8-0.5,2.3,0.3,2.3,1.6V31.4z"
                />
              </g>
            </g>
            <g class="st1">
              <g class="st1">
                <path
                  class="st20"
                  d="M58.9,30.2c0,1.6-1.4,3-3,3c-1.6,0-3-1.4-3-3v-5.5c0-1.6,1.4-3,3-3c1.6,0,3,1.4,3,3V30.2z M56.8,24.2
              c0-0.5-0.5-0.9-0.9-0.9c-0.5,0-0.9,0.5-0.9,0.9v6.5c0,0.5,0.5,0.9,0.9,0.9c0.5,0,0.9-0.5,0.9-0.9V24.2z"
                />
              </g>
            </g>
            <g class="st1">
              <g class="st1">
                <path
                  class="st20"
                  d="M55.9,21.7c-1.6,0-3,1.4-3,3v5.5c0,1.6,1.4,3,3,3c1.6,0,3-1.4,3-3v-5.5C58.9,23.1,57.6,21.7,55.9,21.7z
              M58.5,30c0,1.5-1.2,2.7-2.6,2.7c-1.5,0-2.6-1.2-2.6-2.7v-4.9c0-1.5,1.2-2.7,2.6-2.7c1.5,0,2.6,1.2,2.6,2.7V30z"
                />
              </g>
            </g>
            <g class="st2">
              <g class="st2">
                <path class="st21" d="M62.8,17.4h-2v15.5h2V17.4z" />
              </g>
            </g>
            <g class="st2">
              <g class="st2">
                <g class="st2">
                  <path
                    class="st21"
                    d="M2.2,2v24c0,13.5,9.7,19.5,18.9,23.8C30.2,45.5,40,39.5,40,26V2H2.2z M24.5,29.5l-3.4,14.4l-3.4-14.4
                L3.7,33.8l10.7-10.1L3.7,13.6l14.1,4.2l3.4-14.3l3.4,14.3l14.1-4.2L28,23.7l10.7,10.1L24.5,29.5z"
                  />
                </g>
              </g>
            </g>
            <path
              class="st22"
              d="M2.2,2v24c0,13.5,9.7,19.5,18.9,23.8C30.2,45.5,40,39.5,40,26V2H2.2z M24.5,29.5l-3.4,14.4l-3.4-14.4
          L3.7,33.8l10.7-10.1L3.7,13.6l14.1,4.2l3.4-14.3l3.4,14.3l14.1-4.2L28,23.7l10.7,10.1L24.5,29.5z"
            />
            <path
              class="st22"
              d="M55.9,21.7c-1.6,0-3,1.4-3,3v0.6v4.8c0,1.6,1.4,3,3,3c1.1,0,2.1-0.6,2.6-1.5c0.3-0.5,0.4-0.9,0.4-1.5v-5.5
          C58.9,23.1,57.6,21.7,55.9,21.7z M56.8,30.7c0,0.5-0.5,0.9-0.9,0.9c-0.5,0-0.9-0.5-0.9-0.9v-3.2v-3.3c0-0.5,0.5-0.9,0.9-0.9
          c0.5,0,0.9,0.5,0.9,0.9v5.4V30.7z"
            />
            <path class="st22" d="M62.8,17.4h-2v15.5h2V17.4z" />
            <path
              class="st22"
              d="M47.5,17c-1.2,0-2.2,0.5-2.8,1.5C44.2,19,44,19.7,44,20.5v9.2c0,1.9,1.5,3.5,3.4,3.5c1.9,0,3.4-1.5,3.4-3.5
          v-5h-3.4v1.8h1.5v3.5c0,0.8-0.6,1.5-1.5,1.5S46,30.9,46,30.1v-9.9c0-0.2,0.1-0.5,0.2-0.6c0.3-0.5,0.7-0.8,1.3-0.8
          c0.8,0,1.5,0.6,1.5,1.5v1.5v1.1h1.3h0.7v-2.4C50.9,18.5,49.4,17,47.5,17z"
            />
            <path
              class="st22"
              d="M68.9,17.4v5.5h-0.1c-0.4-0.6-0.9-1.2-1.7-1.2c-1.2,0-2.1,0.9-2.1,2.1v7.3c0,1.2,0.9,2.1,2.1,2.1
          c0.8,0,1.4-0.5,1.7-1.1h0.1l0.1,0.8h1.9V17.4H68.9z M68.9,30.7c0,0.5-0.5,0.9-0.9,0.9c-0.5,0-0.9-0.5-0.9-0.9v-6.5
          c0-0.5,0.5-0.9,0.9-0.9c0.5,0,0.9,0.5,0.9,0.9V30.7z"
            />
            <path
              class="st22"
              d="M66.1,35.3H67v2.4h0.3v-2.4h0.9v-0.2h-2.1V35.3z"
            />
            <path
              class="st22"
              d="M69.6,37.4l-0.9-2.3h-0.4v2.5h0.3v-2.2l0.9,2.2h0.2l0.9-2.2v2.2h0.2v-2.5h-0.3L69.6,37.4z"
            />

            <linearGradient
              id="SVGID_1_"
              gradientUnits="userSpaceOnUse"
              x1="21.0799"
              y1="-127.4928"
              x2="21.0799"
              y2="-94.655"
              gradientTransform="matrix(1 0 0 -1 0 -66.0939)"
            >
              <stop offset="0" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </linearGradient>
            <path
              class="st4"
              d="M2.2,2v24c0,13.5,9.7,19.5,18.9,23.8C30.2,45.5,40,39.5,40,26V2H2.2z M24.5,29.5l-3.4,14.4l-3.4-14.4L3.7,33.8
          l10.7-10.1L3.7,13.6l14.1,4.2l3.4-14.3l3.4,14.3l14.1-4.2L28,23.7l10.7,10.1L24.5,29.5z"
            />

            <linearGradient
              id="SVGID_00000071534471866173669210000014846006625393866626_"
              gradientUnits="userSpaceOnUse"
              x1="-20.0296"
              y1="-72.2322"
              x2="50.9599"
              y2="-72.2322"
              gradientTransform="matrix(1 0 0 -1 0 -66.0939)"
            >
              <stop offset="0" style="stop-color:#FFFFFF;stop-opacity:0" />
              <stop offset="0.5" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </linearGradient>
            <path
              style="fill:url(#SVGID_00000071534471866173669210000014846006625393866626_);"
              d="M8.6,5.7c4,0,7.9,0.4,11.7,1l0.1-0.2
          c-3.8-0.7-7.7-1-11.8-1c-2.2,0-4.4,0.1-6.4,0.3v0.3C4.4,5.8,6.5,5.7,8.6,5.7z"
            />

            <linearGradient
              id="SVGID_00000159445852990280969490000013981799113652422568_"
              gradientUnits="userSpaceOnUse"
              x1="-20.0308"
              y1="-76.2309"
              x2="50.9594"
              y2="-76.2309"
              gradientTransform="matrix(1 0 0 -1 0 -66.0939)"
            >
              <stop offset="0" style="stop-color:#FFFFFF;stop-opacity:0" />
              <stop offset="0.5" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </linearGradient>
            <path
              style="fill:url(#SVGID_00000159445852990280969490000013981799113652422568_);"
              d="M22,7c6.4,1.3,12.5,3.5,18.1,6.5v-0.3
          c-5.6-3-11.7-5.3-18.2-6.5L22,7z"
            />

            <linearGradient
              id="SVGID_00000016040052372514931950000009543127468426063238_"
              gradientUnits="userSpaceOnUse"
              x1="-20.0588"
              y1="-89.8164"
              x2="50.9566"
              y2="-89.8164"
              gradientTransform="matrix(1 0 0 -1 0 -66.0939)"
            >
              <stop offset="0" style="stop-color:#FFFFFF;stop-opacity:0" />
              <stop offset="0.5" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </linearGradient>
            <path
              style="fill:url(#SVGID_00000016040052372514931950000009543127468426063238_);"
              d="M55,24.5v-0.3c-0.5-0.5-0.9-0.9-1.5-1.4
          c0,0.1-0.1,0.1-0.1,0.2C53.9,23.5,54.5,24.1,55,24.5z"
            />

            <linearGradient
              id="SVGID_00000012431671538225710590000009462980113915317166_"
              gradientUnits="userSpaceOnUse"
              x1="-20.0301"
              y1="-93.5423"
              x2="50.958"
              y2="-93.5423"
              gradientTransform="matrix(1 0 0 -1 0 -66.0939)"
            >
              <stop offset="0" style="stop-color:#FFFFFF;stop-opacity:0" />
              <stop offset="0.5" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </linearGradient>
            <path
              style="fill:url(#SVGID_00000012431671538225710590000009462980113915317166_);"
              d="M56.8,26.4c0.7,0.7,1.4,1.5,2,2.3v-0.3
          c-0.6-0.7-1.4-1.5-2-2.3V26.4z"
            />

            <linearGradient
              id="SVGID_00000142878335644810698370000005083943753003472294_"
              gradientUnits="userSpaceOnUse"
              x1="-19.9797"
              y1="-97.9045"
              x2="50.9646"
              y2="-97.9045"
              gradientTransform="matrix(1 0 0 -1 0 -66.0939)"
            >
              <stop offset="0" style="stop-color:#FFFFFF;stop-opacity:0" />
              <stop offset="0.5" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </linearGradient>
            <path
              style="fill:url(#SVGID_00000142878335644810698370000005083943753003472294_);"
              d="M62.2,32.9h0.3c-0.5-0.7-1.1-1.5-1.6-2.2
          v0.4C61.3,31.6,61.7,32.3,62.2,32.9z"
            />

            <linearGradient
              id="SVGID_00000080164579070557550220000003100202688990848430_"
              gradientUnits="userSpaceOnUse"
              x1="-20.0535"
              y1="-85.1364"
              x2="50.959"
              y2="-85.1364"
              gradientTransform="matrix(1 0 0 -1 0 -66.0939)"
            >
              <stop offset="0" style="stop-color:#FFFFFF;stop-opacity:0" />
              <stop offset="0.5" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </linearGradient>
            <path
              style="fill:url(#SVGID_00000080164579070557550220000003100202688990848430_);"
              d="M50.9,20.9v-0.3c-1.5-1.2-3-2.4-4.5-3.5
          c-0.1,0-0.2,0.1-0.3,0.1C47.9,18.5,49.4,19.6,50.9,20.9z"
            />

            <radialGradient
              id="SVGID_00000086692001741037873100000005023408692490348688_"
              cx="-252.7736"
              cy="359.3351"
              r="0.469"
              gradientTransform="matrix(107.778 0 0 -107.899 27260.8633 38802.0273)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </radialGradient>
            <path
              style="fill:url(#SVGID_00000086692001741037873100000005023408692490348688_);"
              d="M20.4,6.5l0.7-3L22,6.7
          c6.4,1.3,12.5,3.5,18.2,6.5V2H2.2v3.7c2.1-0.2,4.3-0.3,6.4-0.3C12.7,5.5,16.6,5.8,20.4,6.5z"
            />

            <radialGradient
              id="SVGID_00000149343549982727437640000005324231062270732429_"
              cx="-252.7737"
              cy="359.3351"
              r="0.469"
              gradientTransform="matrix(107.78 0 0 -107.901 27261.373 38802.7578)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </radialGradient>
            <path
              style="fill:url(#SVGID_00000149343549982727437640000005324231062270732429_);"
              d="M55,24.2c0-0.5,0.5-0.9,0.9-0.9
          c0.5,0,0.9,0.5,0.9,0.9v1.9c0.7,0.7,1.4,1.5,2,2.3v-3.6c0-1.6-1.4-3-3-3c-1,0-1.8,0.5-2.4,1.2C54,23.4,54.5,23.8,55,24.2z"
            />

            <radialGradient
              id="SVGID_00000030451225395233761230000002252817333254727815_"
              cx="-252.7735"
              cy="359.335"
              r="0.469"
              gradientTransform="matrix(107.774 0 0 -107.895 27259.8496 38800.5781)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </radialGradient>
            <path
              style="fill:url(#SVGID_00000030451225395233761230000002252817333254727815_);"
              d="M62.9,32.9V17.4h-2v13.4
          c0.5,0.7,1.1,1.5,1.6,2.2H62.9z"
            />

            <radialGradient
              id="SVGID_00000086654647991331108720000006638507062651716502_"
              cx="-252.774"
              cy="359.3359"
              r="0.469"
              gradientTransform="matrix(107.802 0 0 -107.923 27266.9688 38810.7422)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </radialGradient>
            <path
              style="fill:url(#SVGID_00000086654647991331108720000006638507062651716502_);"
              d="M50.9,20.5c0-1.9-1.5-3.5-3.4-3.5
          c-0.4,0-0.7,0.1-1.1,0.2c1.5,1.1,3.1,2.3,4.5,3.5V20.5z"
            />

            <radialGradient
              id="SVGID_00000005241363243263960820000001401523078307046299_"
              cx="-252.7734"
              cy="359.3346"
              r="0.469"
              gradientTransform="matrix(107.763 0 0 -107.884 27257.0547 38796.5898)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" style="stop-color:#FFFFFF" />
              <stop offset="1" style="stop-color:#FFFFFF;stop-opacity:0" />
            </radialGradient>
            <path
              style="fill:url(#SVGID_00000005241363243263960820000001401523078307046299_);"
              d="M67.1,33.2c0.8,0,1.4-0.5,1.7-1.1h0.1
          l0.1,0.8h1.9V17.4h-2v5.5h-0.1c-0.4-0.6-0.9-1.2-1.7-1.2c-1.2,0-2.1,0.9-2.1,2.1v7.3C65,32.3,65.9,33.2,67.1,33.2z M67,24.2
          c0-0.5,0.5-0.9,0.9-0.9c0.5,0,0.9,0.5,0.9,0.9v6.5c0,0.5-0.5,0.9-0.9,0.9c-0.5,0-0.9-0.5-0.9-0.9V24.2z"
            />
            <g class="st18">
              <g class="st18">
                <path
                  class="st35"
                  d="M39.8,2.3V26c0,2.8-0.4,5.3-1.2,7.5l-10.3-9.7l10.5-9.9l0.8-0.8l-1.1,0.4l-13.8,4.1l-3.4-14l-0.3-1.1
              l-0.3,1.1l-3.4,14L3.7,13.4L2.7,13l0.8,0.8L14,23.7L3.7,33.4c-0.7-2.2-1.2-4.6-1.2-7.5V2.3H39.8z M24.7,29.9l13.7,4.2
              c-2.8,7.6-9.8,12-17.2,15.4C13.7,46.1,6.7,41.7,3.8,34l13.7-4.1L20.9,44l0.3,1.1l0.3-1.1L24.7,29.9z M40.1,2H2.2v24
              c0,13.5,9.7,19.5,18.9,23.8C30.2,45.5,40,39.5,40,26L40.1,2L40.1,2z M17.7,17.8l3.4-14.3l3.4,14.3l14.1-4.2L28,23.7l10.7,10.1
              l-14.2-4.3l-3.4,14.4l-3.4-14.4L3.7,33.8l10.7-10.1L3.7,13.6L17.7,17.8z"
                />
              </g>
            </g>
            <g class="st2">
              <g class="st2">
                <path
                  class="st36"
                  d="M55.9,22c1.5,0,2.7,1.2,2.7,2.7v5.5c0,1.5-1.2,2.7-2.7,2.7c-1.5,0-2.7-1.2-2.7-2.7v-5.5
              C53.2,23.3,54.4,22,55.9,22z M55.9,31.9c0.6,0,1.2-0.5,1.2-1.2v-6.5c0-0.6-0.5-1.2-1.2-1.2c-0.6,0-1.2,0.5-1.2,1.2v6.5
              C54.8,31.4,55.3,31.9,55.9,31.9z M55.9,21.7c-1.6,0-3,1.4-3,3v5.5c0,1.6,1.4,3,3,3c1.6,0,3-1.4,3-3v-5.5
              C58.9,23.1,57.6,21.7,55.9,21.7z M55.9,31.6c-0.5,0-0.9-0.5-0.9-0.9v-6.5c0-0.5,0.5-0.9,0.9-0.9c0.5,0,0.9,0.5,0.9,0.9v6.5
              C56.8,31.2,56.5,31.6,55.9,31.6z"
                />
              </g>
              <g class="st2">
                <path
                  class="st36"
                  d="M62.7,17.6v15h-1.5v-15H62.7z M62.9,17.4h-2v15.5h2V17.4z"
                />
              </g>
              <g class="st2">
                <path
                  class="st36"
                  d="M47.5,17.3c1.7,0,3.2,1.5,3.2,3.2v2.1h-1.5v-2.5c0-0.9-0.7-1.7-1.7-1.7c-0.9,0-1.7,0.7-1.7,1.7V30
              c0,0.9,0.7,1.7,1.7,1.7c0.9,0,1.7-0.7,1.7-1.7v-3.5v-0.3h-0.3h-1.2V25h2.9v4.7c0,1.7-1.5,3.2-3.2,3.2c-1.7,0-3.2-1.5-3.2-3.2
              v-9.2C44.3,18.7,45.8,17.3,47.5,17.3z M47.5,17c-1.9,0-3.4,1.5-3.4,3.5v9.2c0,1.9,1.5,3.5,3.4,3.5c1.9,0,3.4-1.5,3.4-3.5v-5h-3.4
              v1.8h1.5V30c0,0.8-0.6,1.5-1.5,1.5S46,30.8,46,30v-9.8c0-0.8,0.6-1.5,1.5-1.5s1.5,0.6,1.5,1.5v2.7h2v-2.4
              C50.9,18.5,49.4,17,47.5,17z"
                />
              </g>
              <g class="st2">
                <path
                  class="st36"
                  d="M70.7,17.6v15h-1.4L69.3,32v-0.3H69h-0.1h-0.3l-0.1,0.2c-0.3,0.5-0.7,1-1.5,1c-1,0-1.8-0.8-1.8-1.8v-7.3
              c0-1,0.8-1.8,1.8-1.8c0.7,0,1.3,0.5,1.5,1l0.1,0.2h0.2h0.1h0.3v-0.3v-5.3H70.7z M68,31.9c0.6,0,1.2-0.5,1.2-1.2v-6.5
              c0-0.6-0.5-1.2-1.2-1.2c-0.6,0-1.2,0.5-1.2,1.2v6.5C66.7,31.4,67.3,31.9,68,31.9z M71,17.4h-2v5.5h-0.1c-0.4-0.6-0.9-1.2-1.7-1.2
              c-1.2,0-2.1,0.9-2.1,2.1v7.3c0,1.2,0.9,2.1,2.1,2.1c0.8,0,1.4-0.5,1.7-1.1H69l0.1,0.8H71V17.4z M68,31.6c-0.5,0-0.9-0.5-0.9-0.9
              v-6.5c0-0.5,0.5-0.9,0.9-0.9c0.5,0,0.9,0.5,0.9,0.9v6.5C68.9,31.2,68.5,31.6,68,31.6z"
                />
              </g>
            </g>
          </g>
        </svg>
      `;
    }

    return ``;
  }
}
